import * as Cesium from 'cesium'
import { Cartesian3, CallbackProperty, StripeMaterialProperty, VerticalOrigin, Color, StripeOrientation } from 'cesium'
import CesiumNavigation from 'cesium-navigation-es6'
import drone from '/@/assets/icons/drone.png'
import { closeContextMenu, entityFlyToBoundingSphere, handleOpenSharePoint, setPostion, sharePane, state } from './use-cesium-map'
import { e } from '/@/utils/egms96'
import emitter from '/@/event-bus'
import { MapElementEnum } from '/@/constants/map'

// 常量定义
export const IDEAL_BASE_PITCH = -90
export const BASE_FOVBYMAP = 60
export const BASE_PITCH = 0
export const BASE_FOV = 2
export const BASE_HEADING = 0.0
export const BASE_ROLL = 0.0
export const BASE_FAR = 200
export const BASE_NEAR = 5
export const MAX_ZOOM = 200

// 计算当前FOV
export const calculateFov = (zoomLevel: number): number => {
  const clampedZoom = Math.max(1, Math.min(zoomLevel, MAX_ZOOM))
  return BASE_FOVBYMAP / clampedZoom
}

// 点位高度线渲染
const stripeMaterialPropertyOption = {
  evenColor: Color.WHITE.withAlpha(0.5),
  oddColor: Color.TRANSPARENT,
  repeat: 8,
  orientation: StripeOrientation.HORIZONTAL,
}

// 多点位连线渲染
const polylineOption = {
  width: 3,
  material: Color.fromCssColorString('rgb(29, 168, 203)'),
}

export interface PolylineAttributes {
  color: string;
}

export interface PolygonAttributes {
  color: string;
}

export interface PointAttributes {
  color: string;
  text?: string;
  imgUrl?: string;
}

// 定义点实体扩展接口
interface PointEntity extends Cesium.Entity {
  pointIndex?: number;
  typeInfo?: string;
  parent: Cesium.Entity;
}

// 定义线实体扩展接口
interface PolylineEntity extends Cesium.Entity {
  attrColor?: string;
  typeInfo?: string;
  latlngList?: { longitude: number; latitude: number; height: number }[];
  attr?: {
    horizontalDis?: number;
    linearDis?: number;
    heightDiff?: number;
  };
  isFromAPI?: boolean;
}

// 定义多边形实体扩展接口
interface PolygonEntity extends Cesium.Entity {
  attrColor?: string;
  typeInfo?: string;
  latlngList?: { longitude: number; latitude: number; height: number }[];
  attr?: {
    horizontalDis?: number;
    horizontalArea?: number;
    heightDiff?: number;
  };
  isFromAPI?: boolean;
}
export interface APIResponseData {
  id: string;
  name: string;
  resource: {
    type: number;
    content: {
      type: 'Feature';
      properties: {
        color?: string;
        clampToGround?: boolean;
        [key: string]: any;
      };
      geometry: {
        type: 'Point' | 'LineString' | 'Polygon' | 'Circle';
        radius:number;
        coordinates: number[] | number[][] | number[][][];
      };
    };
    user_name: string;
  };
}

// 定义图形实体接口
export interface GraphicalEntity {
  type: string;
  entity: Cesium.Entity;
  positions: Cesium.Cartesian3[];
  attributes: PointAttributes | PolylineAttributes | PolygonAttributes;
  latlngList?: { longitude: number; latitude: number; height: number }[];
  attr?: {
    horizontalDis?: number;
    linearDis?: number;
    heightDiff?: number;
    horizontalArea?: number;
  };
  isFromAPI?: boolean;
  originalColor?: string;
  origin?: APIResponseData;
}

// API返回数据接口

// API图形数据接口
export interface APIGraphicalData {
  id: string;
  type: 'Point' | 'Polyline' | 'Polygon' | 'Circle';
  positions: { longitude: number; latitude: number; height: number }[];
  attributes: PointAttributes | PolylineAttributes | PolygonAttributes;
  properties?: any;
  radius?:number;
  origin?: APIResponseData;
}

// 绘图工具类
export default class DrawTool {
private _dragStartPosition: Cesium.Cartesian3 | null = null
private _isDragHandlersRegistered: boolean = false
  private viewer: Cesium.Viewer;
  private store: any;
  private _drawHandler: Cesium.ScreenSpaceEventHandler | null = null;
  private _selectHandler: Cesium.ScreenSpaceEventHandler | null = null;
  private _dragHandler: Cesium.ScreenSpaceEventHandler | null = null;
  private _hoverHandler: Cesium.ScreenSpaceEventHandler | null = null;
  private _showLocationHandler: Cesium.ScreenSpaceEventHandler | null = null;
  private _dataSource: Cesium.CustomDataSource;
  private _tempPositions: Cesium.Cartesian3[] = [];
  private _mousePos: Cesium.Cartesian3 | null = null;
  private _drawType: string | null = null;
  private _clickPosition: any = null;
  private entitiesPointOptions: any[] = [];
  private entities: Cesium.Entity[] = [];
  private _drawings: any[] = [];
  private _selectedDrawing: any = null;
  private _isDrawing: boolean = false;
  private _currentDrawingEntity: Cesium.Entity | null = null;
  private currentPrimitivesCamera: Cesium.Camera | null = null;
  private currentPrimitive: any = null;
  private _isSelectionEnabled: boolean = true;
  private _hoveredDrawing: any = null;
  private _infoLocationPanel: HTMLElement | null = null;
  private _showRightMenuPanel: HTMLElement | null = null;
  private _lastHoverTime: number = 0;
  private _hoverDebounce: number = 50;
  private _isContinuousDrawing: boolean = false;
  private _drawingIdCounter: number = 0;
  private _draggablePoints: Cesium.Entity[] = [];
  private _virtualPoints: Cesium.Entity[] = [];
  private _isDragging: boolean = false;
  private _draggingEntity: Cesium.Entity & { clickHandler: Cesium.ScreenSpaceEventHandler } | null = null;
  private _originalPositions: Cesium.Cartesian3[] = [];
  private _draggingIsVirtual: boolean = false;
  private _virtualPointIndex: number = -1;
  private _hasStartingPoint: boolean = false;
  private _startingPoint: Cesium.Cartesian3 | null = null;
  private polylineAttr: PolylineAttributes = { color: '#2d8cf0' };
  private polygonAttr: PolygonAttributes = { color: '#2d8cf0' };
  private circleAttr: PolygonAttributes = { color: '#2d8cf0' };
  private _materialCache = new Map<string, Cesium.PolylineOutlineMaterialProperty>();
  private readonly MIN_RADIUS = 0.1;

  // 交互控制相关属性
  private _dragMoveHandler: Cesium.ScreenSpaceEventHandler | null = null;
  private _dragUpHandler: Cesium.ScreenSpaceEventHandler | null = null;
  private _originalCameraSettings: any = null;
  private _isInteractionsEnabled: boolean = false;
  private _currentInteractionMode: 'none' | 'draw' | 'select' | 'hover' = 'none';
  private _dragFlag: boolean = true;
  private _draggingCircleType: 'center' | 'radius' | null = null
  private _selectionFlag: boolean = true;
  private _hoverFlag: boolean = true;

  // 导航控件实例
  private _navigationControl: any = null;

  // 存储从API加载的图形
  private _apiGraphics: Map<string, GraphicalEntity> = new Map();

  // 绘制状态对象
  private polylineComObj: {
    positionList: Cesium.Cartesian3[];
    movePositions: Cesium.Cartesian3[];
    polylineEntity: PolylineEntity | null;
    pointTextEntity: Cesium.Entity | null;
    pointEntityList: PointEntity[];
    textEntityList: Cesium.Entity[];
    editPointEntityList: PointEntity[];
  } = {
    positionList: [],
    movePositions: [],
    polylineEntity: null,
    pointTextEntity: null,
    pointEntityList: [],
    textEntityList: [],
    editPointEntityList: []
  };

  private polygonComObj: {
    positionList: Cesium.Cartesian3[];
    copyPositionList: Cesium.Cartesian3[];
    movePositions: Cesium.Cartesian3[];
    polygonEntity: PolygonEntity | null;
    pointTextEntity1: Cesium.Entity | null;
    pointTextEntity2: Cesium.Entity | null;
    pointEntityList: PointEntity[];
    textEntityList: Cesium.Entity[];
    editPointEntityList: PointEntity[];
    entityList: any[];
  } = {
    positionList: [],
    copyPositionList: [],
    movePositions: [],
    polygonEntity: null,
    pointTextEntity1: null,
    pointTextEntity2: null,
    pointEntityList: [],
    textEntityList: [],
    editPointEntityList: [],
    entityList: []
  };

// 在 DrawTool 类中添加
private _draggingCirclePointType: 'center' | 'radius' = 'center';

  private circleComObj: {
  centerPosition: Cesium.Cartesian3 | null;
  radiusPosition: Cesium.Cartesian3 | null;
  movePosition: Cesium.Cartesian3 | null;
  circleEntity: Cesium.Entity | null;
  textEntity: Cesium.Entity | null;
  pointEntityList: PointEntity[];
  editPointEntityList: PointEntity[];
} = {
  centerPosition: null,
  radiusPosition: null,
  movePosition: null,
  circleEntity: null,
  textEntity: null,
  pointEntityList: [],
  editPointEntityList: []
};

  // 编辑状态对象
  private editObj: {
    editType: string;
    editSwitch: boolean;
    startEditSwitch: boolean;
    currentEditIndex: number;
    copyPositionList:Cesium.Cartesian3[];
    currentEntity: Cesium.Entity | null;
    currentPos: Cesium.Cartesian3[];
    pointEntityList: PointEntity[];
    editPointEntityList: PointEntity[];
    textEntityList: Cesium.Entity[];
    movePoint: any;
    backPoint?: Cesium.Entity;
    backPolyline?: PolylineEntity;
    backPolygon?: PolygonEntity;
  } = {
    editType: '',
    editSwitch: false,
    startEditSwitch: false,
    currentEditIndex: 0,
    currentEntity: null,
    currentPos: [],
    copyPositionList: [],
    pointEntityList: [],
    editPointEntityList: [],
    textEntityList: [],
    movePoint: null
  };

  // 图形存储
  private graphicalEntities: GraphicalEntity[] = [];
  private currentGraphical: GraphicalEntity | null = null;

  // 样式配置
  private _styles = {
    point: {
      color: Cesium.Color.WHITE,
      pixelSize: 10,
      clampToGround: true,
      disableDepthTestDistance: Number.POSITIVE_INFINITY,
      heightReference: Cesium.HeightReference.NONE,
    },
    polyline: {
      width: 6,
      material: new Cesium.PolylineOutlineMaterialProperty({
        color: Cesium.Color.fromCssColorString(this.polylineAttr.color),
      }),
      clampToGround: true,
    },
    polygon: {
      with: 6,
      material: Cesium.Color.BLUE.withAlpha(0.6),
      // outline: true,
      // outlineColor: Cesium.Color.WHITE,
      // outlineWidth: 2
    },
    hover: {
      point: {
        color: Cesium.Color.BLUE,
        pixelSize: 10,
        outlineColor: Cesium.Color.WHITE,
        outlineWidth: 2,
        clampToGround: true,
      },
      polyline: {
        width: 6,
        clampToGround: true,
        outlineColor: Cesium.Color.WHITE,
        outlineWidth: 3,
        outline: true,
        material: new Cesium.PolylineOutlineMaterialProperty({
          color: Cesium.Color.BLUE,
          outlineColor: Cesium.Color.WHITE,
          outlineWidth: 3
        })
      },
      polygon: {
        with: 6,
        clampToGround: true,
        material: Cesium.Color.BLUE.withAlpha(0.3),
        outlineColor: Cesium.Color.WHITE,
        outerWidth: 2,
        outline: true
      }
    },
    selected: {
      point: {
        color: Cesium.Color.BLUE,
        pixelSize: 10,
        outlineColor: Cesium.Color.WHITE,
        outlineWidth: 3,
        clampToGround: true,
      },
      polyline: {
        width: 6,
        clampToGround: true,
        // outlineColor: Cesium.Color.WHITE,
        // outlineWidth: 3,
        material: new Cesium.PolylineOutlineMaterialProperty({
          color: Cesium.Color.BLUE,
          outlineColor: Cesium.Color.WHITE,
          outlineWidth: 3
        }),
      },
      polygon: {
        with: 6,
        material: Cesium.Color.BLUE.withAlpha(0.6),
        // outlineColor: Cesium.Color.WHITE,
        // outlineWidth: 3,
        // outline: true,
        clampToGround: true,
      }
    }
  }

  /**
   * 构造函数
   */
  constructor (
    viewer: Cesium.Viewer,
    store: any = null,
    infoPanelFlag: boolean = false, // 已移除，设为false
    infoLocationFlag: boolean = true,
    showLocationFlag: boolean = true,
    selectionFlag: boolean = true,
    hoverFlag: boolean = true,
    dragFlag: boolean = true,
    navigationFlag: boolean = true
  ) {
    this.viewer = viewer
    this.store = store
    this._dataSource = new Cesium.CustomDataSource('drawingDataSource')
    this.viewer.dataSources.add(this._dataSource)

    // 保存标志
    this._selectionFlag = selectionFlag
    this._hoverFlag = hoverFlag
    this._dragFlag = dragFlag

    if (infoLocationFlag) this._createInfoLocationPanel(0, 0, 0, true)
    if (showLocationFlag) this._initShowLocation()

    if (selectionFlag) this._initSelection()
    if (hoverFlag) this._initHover()
    // if (dragFlag) this._initDrag()

    // 初始化导航控件
    if (navigationFlag) this._initNavigationControls()

    // 保存原始相机设置
    this._saveOriginalCameraSettings()
  }

  private _getPolylineMaterial (color: string): Cesium.PolylineOutlineMaterialProperty {
    if (!this._materialCache.has(color)) {
      this._materialCache.set(color, new Cesium.PolylineOutlineMaterialProperty({
        color: Cesium.Color.fromCssColorString(color),
      }))
    }
    return this._materialCache.get(color)!
  }

  /**
   * 保存原始相机设置
   */
  private _saveOriginalCameraSettings (): void {
    const controller = this.viewer.scene.screenSpaceCameraController
    this._originalCameraSettings = {
      enableRotate: controller.enableRotate,
      enableTranslate: controller.enableTranslate,
      enableZoom: controller.enableZoom,
      enableTilt: controller.enableTilt,
      enableLook: controller.enableLook,
      depthTestAgainstTerrain: this.viewer.scene.globe.depthTestAgainstTerrain
    }
  }

  public restoreCameraSettings () {
    this._restoreCameraSettings()
  }

  /**
   * 恢复原始相机设置
   */
  private _restoreCameraSettings (): void {
    if (!this._originalCameraSettings) return

    const controller = this.viewer.scene.screenSpaceCameraController
    controller.enableRotate = this._originalCameraSettings.enableRotate
    controller.enableTranslate = this._originalCameraSettings.enableTranslate
    controller.enableZoom = this._originalCameraSettings.enableZoom
    controller.enableTilt = this._originalCameraSettings.enableTilt
    controller.enableLook = this._originalCameraSettings.enableLook
  }

  /**
   * 启用所有交互功能
   */
  public enableInteractions (): void {
    if (this._isInteractionsEnabled) return

    if (this._selectionFlag) this._initSelection()
    if (this._hoverFlag) this._initHover()
    // if (this._dragFlag) this._initDrag()

    this._isInteractionsEnabled = true
    this._currentInteractionMode = 'select'
  }

  /**
   * 禁用所有交互功能
   */
  public disableInteractions (): void {
    if (!this._isInteractionsEnabled) return

    this._destroyInteractionHandlers()
    this._restoreCameraSettings()
    this._isInteractionsEnabled = false
    this._currentInteractionMode = 'none'
  }

  /**
   * 销毁交互处理器
   */
  private _destroyInteractionHandlers (): void {
    // 清理选择处理器
    if (this._selectHandler) {
      this._selectHandler.destroy()
      this._selectHandler = null
    }

    // 清理悬停处理器
    if (this._hoverHandler) {
      this._hoverHandler.destroy()
      this._hoverHandler = null
    }

    // 清理拖动处理器
    if (this._dragHandler) {
      this._dragHandler.destroy()
      this._dragHandler = null
    }

    // 清理拖动过程中的处理器
    this._cleanupDragHandlers()
  }

  /**
   * 初始化导航控件
   */
  private _initNavigationControls (): void {
    try {
      const options = {
        defaultResetView: Cesium.Rectangle.fromDegrees(113.45327513931292, 23.185216096146178, 120000),
        enableCompass: true,
        enableZoomControls: true,
        enableDistanceLegend: true,
        enableCompassOuterRing: true,
        moreOptions: false
      }

      this._navigationControl = new CesiumNavigation(this.viewer, options)
      setTimeout(() => {
        const dom = document.querySelector('.navigation-controls') as HTMLElement
        if (dom) dom.style.display = 'none'
      }, 300)
    } catch (error) {
      console.error('Failed to initialize navigation controls:', error)
    }
  }

  /**
   * 实例放大
   */
  public zoomInViaNavigation (): void {
    this.viewer.camera.zoomIn()
  }

  /**
   * 实例缩小
   */
  public zoomOutViaNavigation (): void {
    this.viewer.camera.zoomOut()
  }

  /**
   * 显示导航控件
   */
  public showNavigationControls (show: boolean = true): void {
    if (!this._navigationControl) return

    const compassElement = document.querySelector('.compass') as HTMLElement
    const distanceLegend = document.querySelector('.distance-legend') as HTMLElement

    const displayValue = show ? 'block' : 'none'

    if (compassElement) compassElement.style.display = displayValue
    if (distanceLegend) distanceLegend.style.display = displayValue
  }

  /**
   * 隐藏导航控件
   */
  public hideNavigationControls (): void {
    this.showNavigationControls(false)
  }

  /**
   * 设置导航控件位置
   */
  public setNavigationPosition (position: { top?: string, right?: string, bottom?: string, left?: string }): void {
    const compassElement = document.querySelector('.compass') as HTMLElement
    const distanceLegend = document.querySelector('.distance-legend') as HTMLElement

    const applyPosition = (element: HTMLElement) => {
      if (position.top) element.style.top = position.top
      if (position.right) element.style.right = position.right
      if (position.bottom) element.style.bottom = position.bottom
      if (position.left) element.style.left = position.left
    }

    if (compassElement) applyPosition(compassElement)
    if (distanceLegend) applyPosition(distanceLegend)
  }

  /**
   * 重置视图到默认位置
   */
  public resetView (): void {
    if (this._navigationControl && this._navigationControl.reset) {
      this._navigationControl.reset()
    } else {
      this.viewer.camera.flyTo({
        destination: Cesium.Cartesian3.fromDegrees(105, 35, 15000000),
        orientation: {
          heading: 0.0,
          pitch: -Math.PI / 2,
          roll: 0.0
        },
        duration: 1.5
      })
    }
  }

  /**
   * 销毁导航控件
   */
  private _destroyNavigationControls (): void {
    if (this._navigationControl && this._navigationControl.destroy) {
      this._navigationControl.destroy()
    }
    this._navigationControl = null
  }

  setLoctionPostion (postion: any): void {
    setPostion(postion)
  }

  private _initShowLocation (): void {
    // 移除已有事件处理器，防止重复绑定
    if (this._showLocationHandler) {
      this._showLocationHandler.destroy()
    }

    this._showLocationHandler = new Cesium.ScreenSpaceEventHandler(this.viewer.scene.canvas)

    // 鼠标移动事件添加节流处理
    const handleMouseMove = this._throttle(async (moveEvent: any) => {
      const newPosition = this._getPickPosition(moveEvent.endPosition)
      if (newPosition && Cesium.Cartesian3.equals(newPosition, Cesium.Cartesian3.ZERO) === false) {
        const position = this.conversion(newPosition)

        this.setLoctionPostion(position)
        const asl = await this._getASLPoint(newPosition, moveEvent)
        this._createInfoLocationPanel(
          position.longitude,
          position.latitude,
          asl.asl,
          true,
        )
      }
    }, 500)

    // 鼠标移动显示位置
    this._showLocationHandler.setInputAction(
      handleMouseMove,
      Cesium.ScreenSpaceEventType.MOUSE_MOVE
    )

    // 右键显示菜单
    this._showLocationHandler.setInputAction((event: any) => {
      if (this._isDrawing || !this._isSelectionEnabled || state.rightClick) return

      const ray = this.viewer.camera.getPickRay(event.position)
      if (!ray) return
      const cartesian = this.viewer.scene.globe.pick(ray, this.viewer.scene)
      if (cartesian && Cesium.Cartesian3.equals(cartesian, Cesium.Cartesian3.ZERO) === false) {
        const rightPosition = this.conversion(cartesian)
        this._createRightMenuPanel(rightPosition, event.position)
      }
    }, Cesium.ScreenSpaceEventType.RIGHT_CLICK)

    // 左键关闭菜单
    this._showLocationHandler.setInputAction(() => {
      this.closeMenuPane()
      closeContextMenu()
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK)
  }

  // 节流函数
  private _throttle (fn: Function, delay: number): Function {
    let lastTime = 0
    return (...args: any[]) => {
      const now = Date.now()
      if (now - lastTime >= delay) {
        fn.apply(this, args)
        lastTime = now
      }
    }
  }

  // 处理可拖拽点拖拽
  private _handleDraggablePointDrag (pointEntity: any, eventPosition: any): void {
    this._isDragging = true
    this._draggingEntity = pointEntity
    this._draggingIsVirtual = false

    // 记录拖拽的是圆心点还是半径点
    if (pointEntity.typeInfo === 'circle_center_point_edit') {
      this._draggingCircleType = 'center'
    } else if (pointEntity.typeInfo === 'circle_radius_point_edit') {
      this._draggingCircleType = 'radius'
    }

    // 存储原始位置
    if (this._selectedDrawing) {
      this._originalPositions = [...this._selectedDrawing.positions]
    }

    // 创建拖动事件处理器
    this._setupDragEventHandlers()
  }

  // 设置拖动事件处理器
  private _setupDragEventHandlers (): void {
    if (!this._draggingEntity) return

    // 创建新的事件处理器
    const clickHandler = new Cesium.ScreenSpaceEventHandler(this.viewer.scene.canvas)
    this._draggingEntity.clickHandler = clickHandler

    // 鼠标移动事件 - 处理拖动
    clickHandler.setInputAction((moveEvent: any) => {
      if (state.currentType === 'PointLine' || state.currentType === 'PolygonPoint' || state.currentType === 'PolygonPoint') return
      if (!this._isDragging || !this._draggingEntity || !this._selectedDrawing) return

      // 获取新位置
      const newPosition = this._getPickPosition(moveEvent.endPosition)
      if (!newPosition) return
      const controller = this.viewer.scene.screenSpaceCameraController
      controller.enableRotate = false
      controller.enableTranslate = false
      controller.enableZoom = false
      controller.enableTilt = false
      controller.enableLook = false

      // 更新拖动点的位置
      this._draggingEntity.position = newPosition

      // 查找点的索引
      let pointIndex = -1
      if (this._draggingIsVirtual) {
        pointIndex = this._virtualPointIndex
      } else {
        pointIndex = this._draggablePoints.findIndex(point => point.id === this._draggingEntity.id)
      }

      if (pointIndex !== -1) {
        // 更新位置数组
        this._selectedDrawing.positions[pointIndex] = newPosition

        // 更新图形实体
        this._updateSelectedDrawingPositions()

        // 更新虚拟点位置（如果是折线或多边形）
        if (this._selectedDrawing.type === 'Polyline' || this._selectedDrawing.type === 'Polygon') {
          this._updateVirtualPoints()
        }

        // 更新图形属性（长度、面积等）
        this._updateDrawingAttributes(this._selectedDrawing)
      }
    }, Cesium.ScreenSpaceEventType.MOUSE_MOVE)

    // 鼠标释放事件 - 结束拖动
    clickHandler.setInputAction((upEvent: any) => {
      if (state.currentType === 'PointLine' || state.currentType === 'PolygonPoint') return
      if (this._isDragging) {
        // 清理事件处理器
        if (clickHandler && !clickHandler.isDestroyed()) {
          clickHandler.removeInputAction(Cesium.ScreenSpaceEventType.MOUSE_MOVE)
          clickHandler.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_CLICK)
          clickHandler.destroy()
        }

        // 重置状态
        this._isDragging = false
        this._draggingIsVirtual = false
        this._virtualPointIndex = -1
        this._draggingEntity = null
        // 禁用相机控制，防止地图移动
        const controller = this.viewer.scene.screenSpaceCameraController
        controller.enableRotate = true
        controller.enableTranslate = true
        controller.enableZoom = true
        controller.enableTilt = true
        controller.enableLook = true

        // 更新API图形数据
        if (this._selectedDrawing && this._selectedDrawing.isFromAPI) {
          this._updateAPIGraphicData(this._selectedDrawing.id)
        }
      }
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK)
  }

  // 更新可拖拽点
  private _updateDraggablePoints (): void {
    this._clearDraggablePoints()

    if (!this._selectedDrawing) return

    // 根据图形类型创建可拖拽点
    const positions = this._getDrawablePositions()
    const parentEntity = this.getDataSourceEntity(this._selectedDrawing.id)

    positions.forEach((position: Cesium.Cartesian3, index: number) => {
      const point = this._dataSource.entities.add({
        position: position,
        point: {
          pixelSize: 12,
          color: Cesium.Color.fromCssColorString(this._selectedDrawing.originalColor),
          heightReference: Cesium.HeightReference.NONE,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
          outlineColor: Cesium.Color.WHITE,
          outlineWidth: 2
        },
        description: `可拖动点 ${index + 1}`
      }) as any

      // 设置类型信息
      point.typeInfo = this._getPointTypeInfo(index)
      point.pointIndex = index

      if (parentEntity) {
        point.parent = parentEntity
      }

      this._draggablePoints.push(point)
    })
  }

  // 获取可拖拽点位置
  private _getDrawablePositions (): Cesium.Cartesian3[] {
    if (!this._selectedDrawing) return []

    const positions = this._selectedDrawing.positions
    if (this._selectedDrawing.type === 'Polygon' && positions.length > 1) {
      // 多边形需要闭合，但编辑点不需要最后一个重复点
      return positions.slice(0, -1)
    }
    return positions
  }

  // 获取点类型信息
  private _getPointTypeInfo (index: number): string {
    if (!this._selectedDrawing) return ''

    switch (this._selectedDrawing.type) {
      case 'Polygon':
        return 'polygon_point'
      case 'Polyline':
        return 'polyline_point'
      case 'Point':
        return 'point'
      case 'Circle':
        return index === 0 ? 'circle_center_point' : 'circle_radius_point'
      default:
        return 'point'
    }
  }

  // 更新图形属性
  private _updateDrawingAttributes (drawing: any): void {
    if (!drawing) return

    switch (drawing.type) {
      case 'Polyline':
        drawing.attr = {
          horizontalDis: this._calculatePolylineLength(drawing.positions),
          linearDis: this._calculatePolylineLength(drawing.positions),
          heightDiff: this._calculateHeightDiff(drawing.positions)
        }
        break
      case 'Polygon':
        drawing.attr = {
          horizontalDis: this._calculatePolylineLength([...drawing.positions, drawing.positions[0]]),
          horizontalArea: this._calculatePolygonArea(drawing.positions),
          heightDiff: this._calculateHeightDiff(drawing.positions)
        }
        break
      case 'Circle':
        if (drawing.positions.length >= 2) {
          const center = drawing.positions[0]
          const radiusPoint = drawing.positions[1]
          const radius = Cesium.Cartesian3.distance(center, radiusPoint)
          const area = Math.PI * radius * radius

          drawing.attr = {
            radius: radius,
            area: area,
            circumference: 2 * Math.PI * radius
          }
        }
        break
    }
  }

  /**
 * 更新API图形数据（修复序列化问题）
 */
  private _updateAPIGraphicData (id: string): void {
    const graphic = this._apiGraphics.get(id)
    if (!graphic) return

    // 获取当前的位置（转换为可序列化的格式）
    const positions = this._serializePositions(this._selectedDrawing?.positions || graphic.positions)
    const latlngList = this._selectedDrawing?.latlngList || graphic.latlngList

    // 创建可序列化的事件数据
    const eventData = {
      ...graphic.origin,
      positions: positions,
    }

    // 序列化事件数据
    const serializedData = this._serializeForPostMessage(eventData)

    // 发送更新事件
    emitter.emit('graphicUpdated', serializedData)
  }

  private _getPickPosition (screenPosition: any): Cesium.Cartesian3 | null {
    // 方法1: 使用 scene.pickPosition（优先）
    let position = this.viewer.scene.pickPosition(screenPosition)
    if (position && this._isValidPosition(position)) {
      return position
    }

    // 方法2: 使用椭球体拾取
    const ellipsoid = this.viewer.scene.globe.ellipsoid
    position = this.viewer.camera.pickEllipsoid(screenPosition, ellipsoid)
    if (position && this._isValidPosition(position)) {
      return position
    }

    // 方法3: 使用 globe.pick（备用）
    const ray = this.viewer.camera.getPickRay(screenPosition)
    if (ray) {
      position = this.viewer.scene.globe.pick(ray, this.viewer.scene)
      if (position && this._isValidPosition(position)) {
        return position
      }
    }

    return null
  }

  private _isValidPosition (position: Cesium.Cartesian3): boolean {
    if (!position) return false

    try {
      const cartographic = Cesium.Cartographic.fromCartesian(position)
      if (!cartographic) return false

      return !isNaN(cartographic.longitude) &&
        !isNaN(cartographic.latitude) &&
        !isNaN(cartographic.height) &&
        Math.abs(cartographic.latitude) <= Math.PI / 2 &&
        Math.abs(cartographic.longitude) <= Math.PI
    } catch (error) {
      return false
    }
  }

  // 添加方法：将虚拟点转换为正式航点样式
  private _convertVirtualToRealPoint (virtualPoint: any, position: Cesium.Cartesian3): void {
    if (!virtualPoint || !virtualPoint.point) return

    // 更新点的样式为正式航点样式
    virtualPoint.point.pixelSize = 12
    virtualPoint.point.color = Cesium.Color.WHITE
    virtualPoint.point.outlineColor = Cesium.Color.WHITE
    virtualPoint.point.outlineWidth = 2

    // 更新点的位置
    virtualPoint.position = position

    // 移除虚拟点标记
    virtualPoint._isVirtual = false
  }

  /**
   * 更新虚拟点位置
   */
  private _updateVirtualPoints (): void {
    // 清除所有虚拟点
    this._clearVirtualPoints()

    // 为选中的航线或多边形添加虚拟点
    if (this._selectedDrawing && this._selectedDrawing.isFromAPI) {
      if (this._selectedDrawing.type === 'Polyline' && this._selectedDrawing.positions.length > 1) {
        for (let i = 0; i < this._selectedDrawing.positions.length - 1; i++) {
          this._addVirtualPoint(i, this._selectedDrawing.positions[i], this._selectedDrawing.positions[i + 1])
        }
      } else if (this._selectedDrawing.type === 'Polygon' && this._selectedDrawing.positions.length > 2) {
        const positions = this._selectedDrawing.positions
        for (let i = 0; i < positions.length; i++) {
          const nextIndex = (i + 1) % positions.length
          this._addVirtualPoint(i, positions[i], positions[nextIndex])
        }
      }
    }
  }

  /**
   * 添加虚拟点
   */
  private _addVirtualPoint (index: number, startPosition: Cesium.Cartesian3, endPosition: Cesium.Cartesian3): void {
    const midPoint = new Cesium.Cartesian3()
    Cesium.Cartesian3.add(startPosition, endPosition, midPoint)
    Cesium.Cartesian3.multiplyByScalar(midPoint, 0.5, midPoint)

    const virtualPoint = this._dataSource.entities.add({
      position: midPoint,
      point: {
        pixelSize: 8,
        color: Cesium.Color.WHITE.withAlpha(0.7),
        heightReference: Cesium.HeightReference.NONE,
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
        outlineColor: Cesium.Color.WHITE.withAlpha(0.7),
        outlineWidth: 1
      },
      description: `虚拟点 ${index + 1}`,
    }) as any
    virtualPoint.typeInfo = this._selectedDrawing.type === 'Polygon' ? 'polygon_point_edit' : 'polyline_point_edit'

    virtualPoint._isVirtual = true
    virtualPoint._index = index

    this._virtualPoints.push(virtualPoint)
  }

  /**
   * 创建可拖动点
   */
  private _createDraggablePoints (): void {
    this._clearDraggablePoints()
    this._clearVirtualPoints()

    if (!this._selectedDrawing) return

    const positions = this._getDrawablePositions()
    const parentEntity = this.getDataSourceEntity(this._selectedDrawing.id)

    // 圆形特殊处理
    if (this._selectedDrawing.type !== 'Circle') {
    // 其他图形类型的原有处理逻辑
      positions.forEach((position: Cesium.Cartesian3, index: number) => {
        const point = this._dataSource.entities.add({
          position: position,
          point: {
            pixelSize: 12,
            color: Cesium.Color.WHITE,
            heightReference: Cesium.HeightReference.NONE,
            disableDepthTestDistance: Number.POSITIVE_INFINITY,
            outlineColor: Cesium.Color.WHITE,
            outlineWidth: 2
          },
          description: `可拖动点 ${index + 1}`
        }) as any

        point.typeInfo = this._getPointTypeInfo(index)
        point.pointIndex = index

        if (parentEntity) {
          point.parent = parentEntity
        }

        this._draggablePoints.push(point)
      })
    }
  }

  /**
   * 清除所有虚拟点
   */
  private _clearVirtualPoints (): void {
    this._virtualPoints.forEach(point => {
      this._dataSource.entities.remove(point)
    })
    this._virtualPoints = []
  }

  /**
   * 清除所有可拖动点
   */
  private _clearDraggablePoints (): void {
    this._draggablePoints.forEach(point => {
      this._dataSource.entities.remove(point)
    })
    this._draggablePoints = []

    // 同时清除虚拟点
    this._clearVirtualPoints()
  }

  private _createRightMenuPanel (event: any, position: any): void {
    if (this._showRightMenuPanel) {
      this.viewer.container.removeChild(this._showRightMenuPanel)
    }
    this._showRightMenuPanel = document.createElement('div')
    this._showRightMenuPanel.className = 'context-menu'
    this._showRightMenuPanel.style.left = `${position.x + 10}px`
    this._showRightMenuPanel.style.top = `${position.y + 10}px`

    const soldierTask = document.createElement('div')
    soldierTask.className = 'menu-item'
    soldierTask.innerText = '单兵任务'
    soldierTask.onclick = () => {
      this.closeMenuPane()
    }
    this._showRightMenuPanel.appendChild(soldierTask)

    const sharePoint = document.createElement('div')
    sharePoint.className = 'menu-item'
    sharePoint.innerText = '分享点位'
    sharePoint.onclick = () => {
      handleOpenSharePoint({ longitude: event.longitude, latitude: event.latitude })
      this.closeMenuPane()
    }
    this._showRightMenuPanel.appendChild(sharePoint)

    const flyToHere = document.createElement('div')
    flyToHere.className = 'menu-item'
    flyToHere.innerText = '飞向此处'
    flyToHere.onclick = () => {
      // this.viewer.camera.flyTo({
      //   destination: Cesium.Cartesian3.fromDegrees(event.longitude, event.latitude, 200),
      //   duration: 2
      // })
      if (this.store) {
        this.store.dispatch('flyToPoint', { longitude: event.longitude, latitude: event.latitude })
      }
      this.closeMenuPane()
    }
    this._showRightMenuPanel.appendChild(flyToHere)

    this.viewer.container.appendChild(this._showRightMenuPanel)
  }

  closeMenuPane (): void {
    if (this._showRightMenuPanel) {
      this.viewer.container.removeChild(this._showRightMenuPanel)
      this._showRightMenuPanel = null
    }
  }

  private async _getASLPoint (position: Cesium.Cartesian3, movement: { endPosition: Cesium.Cartesian2 }): Promise<{ asl: string | number, hae: string | number }> {
    const pickRay = this._getPickPosition(movement.endPosition)

    if (!pickRay) return { asl: 0, hae: 0 }

    try {
      const cartographic = Cesium.Cartographic.fromCartesian(pickRay)
      const obj = {
        asl: '',
        hae: ''
      }

      // 确保高度有效
      let finalHeight = cartographic.height
      if (isNaN(finalHeight) || finalHeight < 0) {
        try {
          const terrainHeight = this.viewer.scene.globe.getHeight(cartographic)
          if (terrainHeight !== undefined && !isNaN(terrainHeight)) {
            finalHeight = terrainHeight
          } else {
            finalHeight = 0
          }
        } catch (e) {
          finalHeight = 0
        }
      }

      obj.asl = finalHeight.toFixed(1)
      const te = Cesium.Math.toDegrees(cartographic.longitude)
      const se = Cesium.Math.toDegrees(cartographic.latitude)
      const ae = e(se, te)
      obj.hae = (finalHeight + ae).toFixed(1)
      return obj
    } catch (error) {
      console.error('计算ASL高度失败:', error)
      return { asl: 0, hae: 0 }
    }
  }

  /**
   * 绘制地址坐标系
   */
  private _createInfoLocationPanel (longitude: string | number = '', latitude: string | number = '', height: string | number = '', show: boolean = false): void {
    if (this._infoLocationPanel) {
      this.viewer.container.removeChild(this._infoLocationPanel)
    }
    const Panel = document.createElement('div')
    Panel.className = 'coordinate-panel'

    const content = document.createElement('div')
    content.className = 'coordinate-content'
    Panel.appendChild(content)

    const lon = document.createElement('div')
    lon.className = 'coordinate-item'
    const lon_label = document.createElement('span')
    lon_label.className = 'label'
    lon_label.innerHTML = '经度:'
    lon.appendChild(lon_label)
    const lon_value = document.createElement('span')
    lon_value.className = 'value'
    lon_value.innerHTML = String(Number(longitude).toFixed(6))
    lon.appendChild(lon_value)
    content.appendChild(lon)

    const lat = document.createElement('div')
    lat.className = 'coordinate-item'
    const lat_label = document.createElement('span')
    lat_label.className = 'label'
    lat_label.innerHTML = '纬度:'
    lat.appendChild(lat_label)
    const lat_value = document.createElement('span')
    lat_value.className = 'value'
    lat_value.innerHTML = String(Number(latitude).toFixed(6))
    lat.appendChild(lat_value)
    content.append(lat)

    const hei = document.createElement('div')
    hei.className = 'coordinate-item'
    const hei_label = document.createElement('span')
    hei_label.className = 'label'
    hei_label.innerHTML = '海拔:'
    hei.appendChild(hei_label)
    const hei_value = document.createElement('span')
    hei_value.className = 'value'
    hei_value.innerHTML = height + 'm'
    hei.appendChild(hei_value)
    content.appendChild(hei)

    const location = document.createElement('div')
    location.className = 'coordinate-item'
    const location_label = document.createElement('span')
    location_label.className = 'label'
    location_label.innerHTML = '坐标系:'
    location.appendChild(location_label)
    const location_value = document.createElement('span')
    location_value.className = 'value'
    location_value.innerHTML = 'WGS 84'
    location.appendChild(location_value)
    content.appendChild(location)

    this._infoLocationPanel = Panel
    this._infoLocationPanel.style.display = show ? 'flex' : 'none'
    this.viewer.container.appendChild(this._infoLocationPanel)
  }

  /**
   * 创建信息面板 - 已移除
   */
  private _createInfoPanel (): void {
    // 这个方法已不再需要
    console.warn('_createInfoPanel 方法已废弃，请移除相关调用')
  }

  initSelection (): void {
    if (!this._selectHandler) {
      this._initSelection()
    }
  }

  /**
   * 初始化悬停功能
   */
  private _initHover (): void {
    // 创建悬停事件处理器
    this._hoverHandler = new Cesium.ScreenSpaceEventHandler(this.viewer.scene.canvas)

    // 鼠标移动事件 - 处理悬停高亮
    this._hoverHandler.setInputAction((event: any) => {
      if (state.currentType === 'PointLine' || state.currentType === 'PolygonPoint') return
      if (this._isDrawing) {
        return
      }

      // 使用防抖处理，避免频繁触发
      const now = Date.now()
      if (now - this._lastHoverTime < this._hoverDebounce) {
        return
      }
      this._lastHoverTime = now

      const pickedObject = this.viewer.scene.pick(event.endPosition)

      if (Cesium.defined(pickedObject) && pickedObject.id) {
        const drawing = this._findDrawingByEntity(pickedObject.id)
        if (drawing && drawing !== this._hoveredDrawing) {
          this._clearHoverHighlight()
          this._hoveredDrawing = drawing
          this._highlightDrawing(drawing, true, 'hover')
        } else if (!drawing && this._hoveredDrawing) {
          this._clearHoverHighlight()
        }
      } else if (this._hoveredDrawing) {
        this._clearHoverHighlight()
      }
    }, Cesium.ScreenSpaceEventType.MOUSE_MOVE)
  }

  /**
 * 初始化选择功能
 */
  private _initSelection (): void {
    this._selectHandler = new Cesium.ScreenSpaceEventHandler(this.viewer.scene.canvas)

    // 左键按下开始拖拽
    this._selectHandler.setInputAction((event: any) => {
      if (state.currentType === 'PointLine' || state.currentType === 'PolygonPoint') return

      // 检查是否已经正在拖拽
      if (this._isDragging) {
        return
      }

      const pickedObject = this.viewer.scene.pick(event.position)
      if (!pickedObject || !pickedObject.id) return

      const pickedId = pickedObject.id as PointEntity
      // 检查是否是编辑点、顶点或图形本身
      if (pickedId.typeInfo &&
            (pickedId.typeInfo.includes('_point_edit') ||
             pickedId.typeInfo.includes('_point') ||
             pickedId.typeInfo === 'circle_center_point_edit' ||
             pickedId.typeInfo === 'circle_radius_point_edit'
            )) {
        // 开始拖拽
        this._startDrag(pickedId, event.position)
      }
    }, Cesium.ScreenSpaceEventType.LEFT_DOWN)

    // 鼠标移动处理拖拽
    this._selectHandler.setInputAction((event: any) => {
      if (!this._isDragging) return
      const controller = this.viewer.scene.screenSpaceCameraController
      controller.enableRotate = false
      controller.enableTranslate = false
      controller.enableZoom = false
      controller.enableTilt = false
      controller.enableLook = false

      this._handleDragMove(event.endPosition)
    }, Cesium.ScreenSpaceEventType.MOUSE_MOVE)

    // 左键释放结束拖拽
    this._selectHandler.setInputAction((event: any) => {
      if (this._isDragging) {
        this._endDrag()
      }
    }, Cesium.ScreenSpaceEventType.LEFT_UP)

    // 左键点击其他位置结束拖拽
    this._selectHandler.setInputAction((event: any) => {
      if (state.currentType === 'PointLine' || state.currentType === 'PolygonPoint') return

      // 如果正在拖拽，结束拖拽
      if (this._isDragging) {
        this._endDrag()
        return
      }

      const pickedObject = this.viewer.scene.pick(event.position)
      if (!pickedObject || !pickedObject.id) {
        // 点击空白处，取消选择
        this.deselectDrawing()
        return
      }

      const pickedId = pickedObject.id as PointEntity
      // 处理编辑逻辑
      // 处理圆形相关点击
      if (pickedId.typeInfo === 'circle') {
        const drawing = this.getDrawingByEntity(pickedId)
        if (drawing) {
        // 选择圆形
          this.selectDrawing(drawing)
        }
      } else if (pickedId.typeInfo === 'polyline_point_edit' || pickedId.typeInfo === 'polygon_point_edit') {
        // 编辑点处理逻辑
        this._handleEditPointClick(pickedId)
      } else if (pickedId.typeInfo === 'point' || pickedId.typeInfo === 'circle' ||
            pickedId.typeInfo === 'polyline' || pickedId.typeInfo === 'polygon') {
        // 图形选择逻辑
        this.deselectDrawing()
        const drawing = this.getDrawingByEntity(pickedId)

        if (drawing) {
          this.selectDrawing(drawing)
          this._setupEditMode(drawing, pickedId)
        }
      } else if (pickedId.typeInfo === 'polyline_point' || pickedId.typeInfo === 'polygon_point') {
        // 顶点选择逻辑
        this._handleVertexClick(pickedId)
      }
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK)
  }

  /**
 * 开始拖拽
 */
  private _startDrag (entity: PointEntity, screenPosition: any): void {
    this._isDragging = true
    this._draggingEntity = entity

    // 禁用相机控制，防止地图移动
    const controller = this.viewer.scene.screenSpaceCameraController
    controller.enableRotate = false
    controller.enableTranslate = false
    controller.enableZoom = false
    controller.enableTilt = false
    controller.enableLook = false

    // 存储拖拽开始时的位置
    this._dragStartPosition = this._getPickPosition(screenPosition)

    // 如果是可拖拽点，存储原始位置
    if (this._selectedDrawing) {
      this._originalPositions = this._serializePositions([...this._selectedDrawing.positions])
    }
    this._handleDraggablePointDrag(entity, screenPosition)
    // console.log('开始拖拽:', entity.typeInfo)
  }

  /**
 * 处理拖拽移动
 */
  private _handleDragMove (screenPosition: any): void {
    if (!this._isDragging || !this._draggingEntity) return

    const newPosition = this._getPickPosition(screenPosition)
    if (!newPosition) return

    // 更新拖动点的位置
    this._draggingEntity.position = newPosition

    // console.log(this.viewer.scene.screenSpaceCameraController, 'this.viewer.scene.screenSpaceCameraController')

    // 如果是圆形点，特殊处理
    if (this._selectedDrawing && this._selectedDrawing.type === 'Circle') {
      this._handleCirclePointDrag(this._draggingEntity, newPosition)
      return
    }

    // 实时更新图形
    this._updateDraggedEntity(newPosition)
  }

  /**
 * 处理圆形点拖拽
 */
  private _handleCirclePointDrag (entity: any, newPosition: Cesium.Cartesian3): void {
    if (!this._selectedDrawing || this._selectedDrawing.type !== 'Circle') return

    const drawing = this._selectedDrawing

    if (entity.typeInfo === 'circle_center_point_edit') {
    // 拖拽圆心：移动整个圆形
      const oldCenter = drawing.positions[0]
      const offset = Cesium.Cartesian3.subtract(newPosition, oldCenter, new Cesium.Cartesian3())

      // 更新圆心位置
      drawing.positions[0] = newPosition

      // 更新半径点位置（保持半径不变）
      const newRadiusPoint = Cesium.Cartesian3.add(
        drawing.positions[1],
        offset,
        new Cesium.Cartesian3()
      )
      drawing.positions[1] = newRadiusPoint
    } else if (entity.typeInfo === 'circle_radius_point_edit') {
    // 拖拽半径点：改变半径大小
      drawing.positions[1] = newPosition
    } else if (entity.typeInfo === 'circle_edge_point') {
    // 拖拽边缘点：计算新的半径
      const center = drawing.positions[0]
      const newRadius = Cesium.Cartesian3.distance(center, newPosition)

      // 保持原方向，计算新的半径点
      const oldRadiusPoint = drawing.positions[1]
      const direction = Cesium.Cartesian3.subtract(oldRadiusPoint, center, new Cesium.Cartesian3())
      Cesium.Cartesian3.normalize(direction, direction)

      const newRadiusPoint = Cesium.Cartesian3.add(
        center,
        Cesium.Cartesian3.multiplyByScalar(direction, newRadius, new Cesium.Cartesian3()),
        new Cesium.Cartesian3()
      )

      drawing.positions[1] = newRadiusPoint
    }

    // 更新圆形边界
    this._updateCircleGeometry(drawing)

    // 更新编辑点位置
    this._updateCircleEditPoints()

    // 更新图形属性
    this._updateDrawingAttributes(drawing)

    // 如果是API图形，发送更新事件
    if (drawing.isFromAPI) {
      this._updateAPIGraphicData(drawing.id)
    }
  }

  /**
 * 更新圆形几何边界
 */
  private _updateCircleGeometry (drawing: any): void {
    if (!drawing || drawing.type !== 'Circle') return

    const entity = drawing.entity
    if (!entity) return

    // 确保有足够的位置信息
    if (drawing.positions.length < 2) {
      console.warn('圆形数据不足，无法更新几何边界')
      return
    }

    const center = drawing.positions[0]
    const radiusPoint = drawing.positions[1]

    // 计算半径
    const radius = Cesium.Cartesian3.distance(center, radiusPoint)

    // 确保最小半径
    const validRadius = Math.max(radius, this.MIN_RADIUS)

    try {
      // 更新椭圆实体的半径
      if (entity.ellipse) {
        entity.ellipse.semiMinorAxis = validRadius
        entity.ellipse.semiMajorAxis = validRadius
      }

      // 更新多边形实体的边界
      if (entity.polygon) {
        const circlePositions = this.calculateCirclePoints(center, validRadius)
        entity.polygon.hierarchy = new Cesium.PolygonHierarchy(circlePositions)
      }

      // 更新折线实体的边界
      if (entity.polyline) {
        const circlePositions = this.calculateCirclePoints(center, validRadius)
        entity.polyline.positions = circlePositions
      }

      // 更新圆形属性
      drawing.attr = {
        radius: validRadius,
        area: Math.PI * validRadius * validRadius,
        circumference: 2 * Math.PI * validRadius
      }

      // 更新圆心和半径点的位置（确保在drawing.positions中）
      drawing.positions[0] = center
      if (drawing.positions.length > 1) {
        drawing.positions[1] = radiusPoint
      }

      // 更新API图形数据
      if (drawing.isFromAPI && drawing.apiGraphic?.origin) {
        const origin = drawing.apiGraphic.origin
        origin.resource.content.geometry.radius = validRadius

        // 更新圆心坐标
        const centerLatLng = this.conversion(center)
        origin.resource.content.geometry.coordinates = [
          centerLatLng.longitude,
          centerLatLng.latitude,
          centerLatLng.height
        ]
      }

      // console.log('圆形几何边界已更新，半径:', validRadius.toFixed(2), 'm')
    } catch (error) {
      // console.error('更新圆形几何边界失败:', error)

      // 尝试使用备用方法
      this._updateCircleGeometryFallback(drawing, center, validRadius)
    }
  }

  /**
 * 备用方法：更新圆形几何边界
 */
  private _updateCircleGeometryFallback (drawing: any, center: Cesium.Cartesian3, radius: number): void {
    try {
      const entity = drawing.entity

      // 使用备用的点生成方法
      const circlePositions = this.calculateCirclePointsFallback(center, radius)

      if (entity.polygon) {
        entity.polygon.hierarchy = new Cesium.PolygonHierarchy(circlePositions)
      }

      if (entity.polyline) {
        entity.polyline.positions = circlePositions
      }

      // 更新圆形属性
      drawing.attr = {
        radius: radius,
        area: Math.PI * radius * radius,
        circumference: 2 * Math.PI * radius
      }

      // console.log('使用备用方法更新圆形几何边界')
    } catch (error) {
      console.error('备用方法更新圆形几何边界失败:', error)
    }
  }

  /**
 * 更新圆上的边缘点
 */
  private _updateCircleEdgePoints (center: Cesium.Cartesian3, radius: number): void {
  // 找到所有边缘点并更新位置
    const edgePoints = this._draggablePoints.filter(p => p.typeInfo === 'circle_edge_point')

    edgePoints.forEach((edgePoint, index) => {
      const angle = (index * Math.PI) / 4
      const newPosition = this.calculatePointOnCircle(center, radius, angle)
      edgePoint.position = newPosition
    })
  }

  /**
 * 更新圆形编辑点位置
 */
  private _updateCircleEditPoints (): void {
    if (!this._selectedDrawing || this._selectedDrawing.type !== 'Circle') return

    const drawing = this._selectedDrawing
    const center = drawing.positions[0]
    const radiusPoint = drawing.positions[1]
    const radius = Cesium.Cartesian3.distance(center, radiusPoint)

    // 更新圆心点
    const centerPoint = this._draggablePoints.find(p => p.typeInfo === 'circle_center_point_edit')
    if (centerPoint) {
      centerPoint.position = center
    }

    // 更新半径点
    const radiusPointEntity = this._draggablePoints.find(p => p.typeInfo === 'circle_radius_point_edit')
    if (radiusPointEntity) {
      radiusPointEntity.position = radiusPoint
    }

    // 更新半径文本
    if (this.circleComObj.textEntity) {
      const midPoint = this.calcCenterPosition(center, radiusPoint)
      this.circleComObj.textEntity.position = midPoint
      if (this.circleComObj.textEntity.label) {
        this.circleComObj.textEntity.label.text = `${radius.toFixed(2)}m`
      }
    }

    // 更新边缘点
    this._updateCircleEdgePoints(center, radius)
  }

  /**
 * 高亮显示圆形边缘线
 */
  private _highlightCircleEdge (drawing: any): void {
    const entity = drawing.entity
    if (entity.polyline) {
      const circleColor = Cesium.Color.fromCssColorString(drawing.originalColor)
      // 增加边缘线的宽度和颜色
      entity.polyline.width = 4
      entity.polyline.material = new Cesium.PolylineOutlineMaterialProperty({
        color: circleColor,
        outlineColor: Cesium.Color.WHITE,
        outlineWidth: 2
      })
    }
  }

  /**
 * 计算圆上指定角度的点
 */
  private calculatePointOnCircle (center: Cesium.Cartesian3, radius: number, angle: number): Cesium.Cartesian3 {
    const cartographic = Cesium.Cartographic.fromCartesian(center)
    const centerLon = Cesium.Math.toDegrees(cartographic.longitude)
    const centerLat = Cesium.Math.toDegrees(cartographic.latitude)
    const height = cartographic.height

    // 计算偏移
    const latOffset = (radius / 111320) * Math.cos(angle)
    const lonOffset = (radius / (111320 * Math.cos(centerLat * Math.PI / 180))) * Math.sin(angle)

    const lat = centerLat + latOffset
    const lon = centerLon + lonOffset

    return Cesium.Cartesian3.fromDegrees(lon, lat, height)
  }

  /**
 * 创建圆形编辑相关实体（圆心点、半径点、半径文本）
 */
  private _createCircleEditEntities (): void {
    if (!this._selectedDrawing || this._selectedDrawing.type !== 'Circle') return

    const drawing = this._selectedDrawing
    const center = drawing.positions[0]
    const radiusPoint = drawing.positions[1]
    const radius = Cesium.Cartesian3.distance(center, radiusPoint)
    const parentEntity = drawing.entity

    // 清除已有的编辑点
    this._clearDraggablePoints()
    const circleColor = Cesium.Color.fromCssColorString(this._selectedDrawing.originalColor)
    // 1. 创建圆心点
    const centerPoint = this._dataSource.entities.add({
      position: center,
      point: {
        pixelSize: 10,
        color: circleColor,
        heightReference: Cesium.HeightReference.NONE,
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
        outlineColor: Cesium.Color.WHITE,
        outlineWidth: 3
      },
      description: '圆心点'
    }) as any

    centerPoint.typeInfo = 'circle_center_point_edit'
    centerPoint.pointIndex = 0
    centerPoint.parent = parentEntity
    this._draggablePoints.push(centerPoint)

    // 2. 创建半径点（圆上的一点）
    const radiusPointEntity = this._dataSource.entities.add({
      position: radiusPoint,
      point: {
        pixelSize: 10,
        color: circleColor,
        heightReference: Cesium.HeightReference.NONE,
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
        outlineColor: Cesium.Color.WHITE,
        outlineWidth: 3
      },
      description: '半径点'
    }) as any

    radiusPointEntity.typeInfo = 'circle_radius_point_edit'
    radiusPointEntity.pointIndex = 1
    radiusPointEntity.parent = parentEntity
    this._draggablePoints.push(radiusPointEntity)

    // 3. 创建半径文本
    const midPoint = this.calcCenterPosition(center, radiusPoint)
    const radiusTextEntity = this._dataSource.entities.add({
      position: midPoint,
      label: {
        font: '16px 黑体',
        fillColor: Cesium.Color.WHITE,
        outlineColor: Cesium.Color.BLACK,
        outlineWidth: 4,
        showBackground: true,
        backgroundColor: new Cesium.Color(0.2, 0.2, 0.2, 0.8),
        horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
        verticalOrigin: Cesium.VerticalOrigin.CENTER,
        pixelOffset: new Cesium.Cartesian2(0, 0),
        text: `${radius.toFixed(2)}m`,
        scaleByDistance: new Cesium.NearFarScalar(1000, 1, 500000, 0.8),
        disableDepthTestDistance: 500000
      }
    }) as any

    radiusTextEntity.typeInfo = 'circle_radius_text'
    radiusTextEntity.parent = parentEntity
    this.circleComObj.textEntity = radiusTextEntity

    const radiusLineEntity = this._dataSource.entities.add({
      polyline: {
        positions: new Cesium.CallbackProperty(() => {
          const center = this._selectedDrawing.positions[0]
          const radiusPoint = this._selectedDrawing.positions[1]
          return [center, radiusPoint]
        }, false),
        width: 2,
        material: new Cesium.PolylineDashMaterialProperty({
          color: circleColor,
        })
      }
    })
    radiusLineEntity.typeInfo = 'circle_radius_line'
    radiusLineEntity.parent = parentEntity
    this.circleComObj.circleEntity = radiusLineEntity

    // 4. 创建圆上的边缘点（多个，用于直观显示）
    this._createCircleEdgePoints(center, radiusPoint)

    // 5. 高亮显示边缘线
    this._highlightCircleEdge(drawing)
  }

  /**
 * 创建圆上的边缘点
 */
  private _createCircleEdgePoints (center: Cesium.Cartesian3, radiusPoint: Cesium.Cartesian3): void {
    const radius = Cesium.Cartesian3.distance(center, radiusPoint)

    // 计算圆上的8个点
    const edgePoints: Cesium.Cartesian3[] = []
    for (let i = 0; i < 8; i++) {
      const angle = (i * Math.PI) / 4 // 45度间隔
      const edgePoint = this.calculatePointOnCircle(center, radius, angle)
      edgePoints.push(edgePoint)
    }

    // 创建边缘点实体
    edgePoints.forEach((point, index) => {
      const edgePointEntity = this._dataSource.entities.add({
        position: point,
        point: {
          pixelSize: 10,
          color: Cesium.Color.YELLOW.withAlpha(0.7),
          heightReference: Cesium.HeightReference.NONE,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
          outlineColor: Cesium.Color.WHITE,
          outlineWidth: 2
        },
        description: `边缘点 ${index + 1}`,
        show: false
      }) as any

      edgePointEntity.typeInfo = 'circle_edge_point'
      edgePointEntity.pointIndex = index
      edgePointEntity.parent = this._selectedDrawing?.entity
      this._draggablePoints.push(edgePointEntity)
    })
  }

  // 新增：圆形拖拽更新方法
  private _updateCircleDuringDrag (newPosition: Cesium.Cartesian3): void {
    if (!this._selectedDrawing || this._selectedDrawing.type !== 'Circle') return

    const drawing = this._selectedDrawing
    const pointIndex = this._draggingEntity.pointIndex

    if (pointIndex === 0) {
    // 拖拽圆心点
      drawing.positions[0] = newPosition

      // 保持半径不变，移动半径点
      if (drawing.positions.length > 1) {
        const oldCenter = this._originalPositions[0]
        const oldRadiusPoint = this._originalPositions[1]

        // 计算偏移量
        const offset = Cesium.Cartesian3.subtract(newPosition, oldCenter, new Cesium.Cartesian3())

        // 应用偏移到半径点
        const newRadiusPoint = Cesium.Cartesian3.add(oldRadiusPoint, offset, new Cesium.Cartesian3())
        drawing.positions[1] = newRadiusPoint

        // 更新半径点实体位置
        const radiusPointEntity = this._draggablePoints.find(p => p.pointIndex === 1)
        if (radiusPointEntity) {
          radiusPointEntity.position = newRadiusPoint
        }
      }
    } else if (pointIndex === 1) {
    // 拖拽半径点
      drawing.positions[1] = newPosition
    }

    // 更新圆形边界
    this._updateCircleBoundary(drawing)
  }

  // 优化：计算圆形位置的函数
  private calculateCirclePositions (center: Cesium.Cartesian3, radius: number, segments: number = 64): Cesium.Cartesian3[] {
    const positions: Cesium.Cartesian3[] = []

    // 转换为中心点的经纬度
    const cartographic = Cesium.Cartographic.fromCartesian(center)
    if (!cartographic) return positions

    const centerLon = Cesium.Math.toDegrees(cartographic.longitude)
    const centerLat = Cesium.Math.toDegrees(cartographic.latitude)
    const height = cartographic.height

    for (let i = 0; i <= segments; i++) {
      const angle = (i / segments) * Math.PI * 2

      // 计算偏移
      const latOffset = (radius / 111320) * Math.cos(angle)
      const lonOffset = (radius / (111320 * Math.cos(centerLat * Math.PI / 180))) * Math.sin(angle)

      const lat = centerLat + latOffset
      const lon = centerLon + lonOffset

      // 获取地形高度
      const terrainHeight = this.getTerrainHeight({ lng: lon, lat: lat }, this.viewer)

      positions.push(Cesium.Cartesian3.fromDegrees(lon, lat, terrainHeight || height))
    }

    return positions
  }

  // 新增：更新圆形边界
  private _updateCircleBoundary (drawing: any): void {
    const center = drawing.positions[0]
    const radiusPoint = drawing.positions[1]

    // 计算半径
    const radius = Cesium.Cartesian3.distance(center, radiusPoint)

    // 计算圆形边界点
    const circlePositions = this.calculateCirclePositions(center, radius)

    // 更新实体
    const entity = drawing.entity
    if (entity.polygon) {
      entity.polygon.hierarchy = new Cesium.PolygonHierarchy(circlePositions)
    }
    if (entity.polyline) {
      entity.polyline.positions = circlePositions
    }

    // 更新属性
    drawing.attr = {
      radius: radius,
      area: Math.PI * radius * radius,
      circumference: 2 * Math.PI * radius
    }

    // 更新API数据
    if (drawing.isFromAPI && drawing.apiGraphic?.origin) {
      const origin = drawing.apiGraphic.origin
      origin.resource.content.geometry.radius = radius

      const centerLatLng = this.conversion(center)
      origin.resource.content.geometry.coordinates = [
        centerLatLng.longitude,
        centerLatLng.latitude,
        centerLatLng.height
      ]
    }
  }

  /**
 * 更新被拖拽的实体
 */
  private _updateDraggedEntity (newPosition: Cesium.Cartesian3): void {
    if (!this._draggingEntity || !this._selectedDrawing) return

    const entity = this._draggingEntity
    const drawing = this._selectedDrawing

    // 如果是圆形，特殊处理
    if (drawing.type === 'Circle') {
      this._updateCircleDuringDrag(newPosition)
      return
    }
    // 查找点的索引
    let pointIndex = -1
    const draggableIndex = this._draggablePoints.findIndex(point => point.id === entity.id)
    if (draggableIndex !== -1) {
      pointIndex = draggableIndex
    }

    if (pointIndex !== -1) {
      // 更新位置数组（转换为可序列化的格式）
      const serializedPos = this._serializePositions([newPosition])[0]
      if (serializedPos) {
        // 创建一个新的Cartesian3对象
        const updatedPos = Cesium.Cartesian3.fromDegrees(
          serializedPos.longitude || serializedPos.x,
          serializedPos.latitude || serializedPos.y,
          serializedPos.height || serializedPos.z || 0
        )

        // 更新原始位置数组
        if (this._originalPositions && this._originalPositions[pointIndex]) {
          this._originalPositions[pointIndex] = serializedPos
        }

        // 更新图形的位置数组
        drawing.positions[pointIndex] = updatedPos

        // 更新图形实体
        this._updateSelectedDrawingPositions()

        // 更新虚拟点位置（如果是折线或多边形）
        if (drawing.type === 'Polyline' || drawing.type === 'Polygon') {
          this._updateVirtualPoints()
        }

        // 更新图形属性（长度、面积等）
        this._updateDrawingAttributes(drawing)
      }
    }
  }

  /**
 * 结束拖拽
 */
  private _endDrag (): void {
    if (!this._isDragging) return

    // console.log('结束拖拽')

    // 恢复相机控制
    const controller = this.viewer.scene.screenSpaceCameraController
    controller.enableRotate = true
    controller.enableTranslate = true
    controller.enableZoom = true
    controller.enableTilt = true
    controller.enableLook = true

    // 如果是圆形，确保最终更新
    if (this._selectedDrawing && this._selectedDrawing.type === 'Circle') {
      this._updateCircleBoundary(this._selectedDrawing)
    }

    // 如果是API图形，发送更新事件
    if (this._selectedDrawing && this._selectedDrawing.isFromAPI) {
      this._updateAPIGraphicData(this._selectedDrawing.id)
    }

    // 重置拖拽状态
    this._isDragging = false
    this._draggingEntity = null
    this._draggingIsVirtual = false
    this._draggingCircleType = null
    this._dragStartPosition = null
  }

  /**
 * 处理虚拟点拖拽
 */
  private _handleVirtualPointDrag (virtualPoint: any, newPosition: Cesium.Cartesian3): void {
    if (!this._selectedDrawing) return

    // 1. 将虚拟点转换为正式航点样式
    this._convertVirtualToRealPoint(virtualPoint, newPosition)

    // 2. 把这个点添加到正式点列
    const insertIndex = virtualPoint._index + 1
    this._selectedDrawing.positions.splice(insertIndex, 0, newPosition)

    // 3. 更新图形实体
    this._updateSelectedDrawingPositions()

    // 4. 更新虚拟点列表
    this._updateVirtualPoints()

    // 5. 更新可拖拽点列表
    this._updateDraggablePoints()
  }

  /**
 * 添加一个辅助方法来更新选中的图形位置
 */
  private _updateSelectedDrawingPositions (): void {
    if (!this._selectedDrawing) return

    const entity = this._selectedDrawing.entity
    if (!entity) return

    switch (this._selectedDrawing.type) {
      case 'Polyline':
        if (entity.polyline) {
          entity.polyline.positions = this._selectedDrawing.positions
        }
        break
      case 'Polygon':
        if (entity.polygon) {
          entity.polygon.hierarchy = new Cesium.PolygonHierarchy(this._selectedDrawing.positions)
        }
        // 更新折线边界
        if (entity.polyline) {
          const closedPositions = [...this._selectedDrawing.positions, this._selectedDrawing.positions[0]]
          entity.polyline.positions = closedPositions
        }
        break
      case 'Point':
        if (entity.position) {
          entity.position = this._selectedDrawing.positions[0]
        }
        break
      case 'Circle': {
        break
      }
    }
  }

  twoPointsDistance (firstPoint: Cesium.Cartesian3, secondPoint: Cesium.Cartesian3) {
    return Cesium.Cartesian3.distance(firstPoint, secondPoint)
  }

  // 新增：处理编辑点点击
  private _handleEditPointClick (pointEntity: PointEntity): void {
    // console.log('_handleEditPointClick', pointEntity)
    if (!pointEntity.parent) return

    const parentEntity = pointEntity.parent as PolylineEntity | PolygonEntity
    const drawing = this.getDrawingByEntity(parentEntity)
    if (!drawing) return

    // 获取当前点的位置
    const position = pointEntity.position!.getValue(Cesium.JulianDate.now())

    // 获取父实体的位置数组
    let positions: Cesium.Cartesian3[] = []
    if (parentEntity.polyline) {
      positions = [...(parentEntity.polyline.positions as any).getValue(Cesium.JulianDate.now())]
    } else if (parentEntity.polygon) {
      positions = [...(parentEntity.polygon.hierarchy as any)._value.positions]
    }

    // 插入新点
    const insertIndex = pointEntity.pointIndex! + 1
    positions.splice(insertIndex, 0, position)

    // 更新实体
    if (parentEntity.polyline) {
      parentEntity.polyline.positions = positions
    } else if (parentEntity.polygon) {
      parentEntity.polygon.hierarchy = positions
      // 如果是多边形，还需要更新折线
      parentEntity.polyline!.positions = [...positions, positions[0]]
    }

    // 更新文本和编辑点
    this._updateEditPoints(drawing, positions, pointEntity.typeInfo!)

    // 选择这个新添加的点
    this._setupEditMode(drawing, pointEntity)
  }

  // 新增：设置编辑模式
  private _setupEditMode (drawing: any, entity: any): void {
    this.editObj = {
      editType: entity.typeInfo,
      editSwitch: true,
      startEditSwitch: true,
      currentEditIndex: entity.pointIndex || 0,
      currentEntity: entity,
      currentPos: drawing.positions,
      copyPositionList: [],
      pointEntityList: [],
      editPointEntityList: [],
      textEntityList: [],
      movePoint: entity
    }

    // 根据类型设置当前位置
    switch (entity.typeInfo) {
      case 'point':
        if (entity.position) {
          this.editObj.currentPos = [entity.position.getValue(Cesium.JulianDate.now())]
        }
        break
      case 'circle':
        if (entity.position) {
          this.editObj.currentPos = [entity.position.getValue(Cesium.JulianDate.now())]
        }
        break
      case 'polyline':
        if (entity.polyline) {
          this.editObj.currentPos = [...(entity.polyline.positions as any).getValue(Cesium.JulianDate.now())]
        }
        break
      case 'polygon':
        if (entity.polygon) {
          this.editObj.currentPos = [...(entity.polygon.hierarchy as any)._value.positions]
        }
        break
      case 'polyline_point':
      case 'polygon_point':
        this.editObj.currentEditIndex = entity.pointIndex!
        break
    }
  }

  // 新增：处理顶点点击
  private _handleVertexClick (vertexEntity: PointEntity): void {
    if (!vertexEntity.parent) return

    const parentEntity = vertexEntity.parent as PolylineEntity | PolygonEntity
    const drawing = this.getDrawingByEntity(parentEntity)
    if (!drawing) return

    this.editObj = {
      editType: vertexEntity.typeInfo!,
      editSwitch: true,
      startEditSwitch: true,
      copyPositionList: drawing.positions,
      currentEditIndex: vertexEntity.pointIndex!,
      currentEntity: vertexEntity,
      currentPos: drawing.positions,
      pointEntityList: [],
      editPointEntityList: [],
      textEntityList: [],
      movePoint: vertexEntity
    }

    // 获取位置数据
    if (parentEntity.polyline) {
      this.editObj.currentPos = [...(parentEntity.polyline.positions as any).getValue(Cesium.JulianDate.now())]
    } else if (parentEntity.polygon) {
      this.editObj.currentPos = [...(parentEntity.polygon.hierarchy as any)._value.positions]
    }
  }

  // 新增：更新编辑点
  private _updateEditPoints (drawing: any, positions: Cesium.Cartesian3[], type: string): void {
    // 清除旧的编辑点
    this.editObj.editPointEntityList.forEach(point => {
      this._dataSource.entities.remove(point)
    })
    this.editObj.editPointEntityList = []

    // 创建新的编辑点
    for (let i = 0; i < positions.length - 1; i++) {
      const startPos = positions[i]
      const endPos = positions[i + 1]
      const midPoint = this.calcCenterPosition(startPos, endPos)

      const editPoint = this.addPoint(
        midPoint,
        8,
        new Cesium.Color(1, 1, 1, 0.7),
        i,
        type === 'polyline' ? 'polyline_point_edit' : 'polygon_point_edit',
        drawing.entity
      ) as PointEntity

      this.editObj.editPointEntityList.push(editPoint)
    }
  }

  /**
 * 清理拖拽相关的事件处理器
 */
  private _cleanupDragHandlers (): void {
    // 清理拖动过程中的处理器
    if (this._dragMoveHandler) {
      this._dragMoveHandler.destroy()
      this._dragMoveHandler = null
    }

    if (this._dragUpHandler) {
      this._dragUpHandler.destroy()
      this._dragUpHandler = null
    }

    // 恢复相机控制
    // 禁用相机控制，防止地图移动
    const controller = this.viewer.scene.screenSpaceCameraController
    controller.enableRotate = true
    controller.enableTranslate = true
    controller.enableZoom = true
    controller.enableTilt = true
    controller.enableLook = true
    this._isDragging = false
    this._draggingEntity = null
    this._draggingIsVirtual = false
    this._virtualPointIndex = -1

    // 重置编辑对象
    if (this.editObj) {
      this.editObj.editSwitch = false
      this.editObj.startEditSwitch = false
    }
  }

  /**
 * 转换 Cesium 对象为可序列化的格式
 */
  private _serializeForPostMessage (data: any): any {
    if (!data) return data

    try {
      // 深度复制并移除不可序列化的属性
      return JSON.parse(JSON.stringify(data, (key, value) => {
        // 移除函数、undefined、Cesium对象等不可序列化的值
        if (typeof value === 'function' || value === undefined) {
          return null
        }

        // 处理Cesium对象
        if (value && typeof value === 'object') {
          // 如果是Cartesian3-like对象，转换为数组
          if (value.x !== undefined && value.y !== undefined && value.z !== undefined) {
            return [value.x, value.y, value.z]
          }
          // 如果是Color-like对象，转换为RGBA数组
          if (value.red !== undefined && value.green !== undefined && value.blue !== undefined) {
            return {
              r: value.red,
              g: value.green,
              b: value.blue,
              a: value.alpha || 1
            }
          }
          // 如果是Cesium实体或复杂对象，只保留必要的属性
          if (value instanceof Cesium.Entity ||
                    value._id ||
                    value._name ||
                    value._show) {
            return {
              id: value.id || value._id,
              name: value.name || value._name,
              show: value.show || value._show
            }
          }
        }
        return value
      }))
    } catch (error) {
      console.error('序列化数据失败:', error)
      return { error: '数据序列化失败' }
    }
  }

  /**
 * 转换位置数组为可序列化的格式
 */
  private _serializePositions (positions: Cesium.Cartesian3[]): any[] {
    if (!positions || !Array.isArray(positions)) return []

    return positions.map(pos => {
      if (!pos) return null

      try {
        const cartographic = Cesium.Cartographic.fromCartesian(pos)
        if (cartographic) {
          return {
            longitude: Cesium.Math.toDegrees(cartographic.longitude),
            latitude: Cesium.Math.toDegrees(cartographic.latitude),
            height: cartographic.height
          }
        }
      } catch (error) {
        console.warn('转换位置失败:', error)
      }

      // 如果转换失败，尝试直接获取坐标
      if (pos.x !== undefined && pos.y !== undefined && pos.z !== undefined) {
        return {
          x: pos.x,
          y: pos.y,
          z: pos.z
        }
      }

      return null
    }).filter(pos => pos !== null)
  }

  // 添加获取和设置 editObj 的方法
  public getEditObj (): any {
    return this.editObj
  }

  public setEditObj (editObj: any): void {
    this.editObj = editObj
  }

  // 重置编辑状态
  public resetEditMode (): void {
    this.editObj = null
    this._isDragging = false
    this._draggingEntity = null
  }

  /**
   * 选择图形
   */
  selectDrawing (drawing: any): void {
    if (this._selectedDrawing && this._selectedDrawing.id === drawing.id) return

    this.deselectDrawing()
    this._selectedDrawing = drawing
    this._highlightDrawing(drawing, true, 'select')

    // 更新store状态
    this._updateStoreWithSelectedDrawing(drawing)

    // 为所有类型的图形添加可拖动点
    this._createDraggablePoints()

    // 如果是圆形，创建额外的编辑实体
    if (drawing.type === 'Circle') {
      this._createCircleEditEntities()
    }

    // 只有来自API的折线或多边形才需要虚拟点
    if (drawing.isFromAPI && (drawing.type === 'Polyline' || drawing.type === 'Polygon')) {
      this._updateVirtualPoints()
    }
    if (this.store) {
      this.store.commit('SET_SELECTED_DRAWING', this._serializeForPostMessage(drawing))
    }
  }

  /**
 * 更新store状态（修复序列化问题）
 */
  private async _updateStoreWithSelectedDrawing (drawing: any): Promise<void> {
    if (!this.store) return

    // 重置所有状态
    this.store.commit('SET_SHOW_POINT', false)
    this.store.commit('SET_CURRENT_POINT', {})
    this.store.commit('SET_SHOW_POLYLINE', false)
    this.store.commit('SET_CURRENT_POLYLINE', {})
    this.store.commit('SET_SHOW_POLYGON', false)
    this.store.commit('SET_CURRENT_POLYGON', {})
    this.store.commit('SET_SHOW_CIRCLE', false)
    this.store.commit('SET_CURRENT_CIRCLE', {})

    const data = drawing.apiGraphic
    if (!data) return

    // 转换数据为可序列化的格式
    const row = await this.convertGraphicalDataToAPIResponse(data)

    // 序列化数据避免postMessage错误
    const serializableRow = this._serializeForPostMessage(row)

    // 根据图形类型设置对应的状态
    switch (drawing.type) {
      case 'Point': {
        this.store.commit('SET_SHOW_POINT', true)
        this.store.commit('SET_CURRENT_POINT', serializableRow)
        break
      }
      case 'Polyline':
        this.store.commit('SET_SHOW_POLYLINE', true)
        this.store.commit('SET_CURRENT_POLYLINE', serializableRow)
        break
      case 'Polygon':
        this.store.commit('SET_SHOW_POLYGON', true)
        this.store.commit('SET_CURRENT_POLYGON', serializableRow)
        break
      case 'Circle':
        this.store.commit('SET_SHOW_CIRCLE', true)
        this.store.commit('SET_CURRENT_CIRCLE', serializableRow)
        break
    }
  }

  /**
   * 序列化数据以避免postMessage错误
   */
  private serializeForPostMessage (data: any): any {
    if (!data) return {}

    try {
      // 深度复制并移除不可序列化的属性
      const cloned = JSON.parse(JSON.stringify(data, (key, value) => {
        // 移除函数、undefined等不可序列化的值
        if (typeof value === 'function' || value === undefined) {
          return null
        }
        // 处理Cesium对象
        if (value && typeof value === 'object') {
          // 如果是Cartesian3-like对象，转换为数组
          if (value.x !== undefined && value.y !== undefined && value.z !== undefined) {
            return [value.x, value.y, value.z]
          }
          // 如果是Color-like对象，转换为RGBA数组
          if (value.red !== undefined && value.green !== undefined && value.blue !== undefined) {
            return {
              r: value.red,
              g: value.green,
              b: value.blue,
              a: value.alpha || 1
            }
          }
        }
        return value
      }))

      return cloned
    } catch (error) {
      console.error('序列化数据失败:', error)
      return { id: data.id || 'unknown', error: '序列化失败' }
    }
  }

  /**
   * 取消选择图形
   */
  deselectDrawing (): void {
    if (!this._selectedDrawing) return

    this._highlightDrawing(this._selectedDrawing, false, 'select')

    // 清除可拖动点和虚拟点
    this._clearDraggablePoints()
    this._clearVirtualPoints()

    // 恢复圆形边缘线样式
    if (this._selectedDrawing.type === 'Circle') {
      this._restoreDrawingStyle(this._selectedDrawing)
    }

    // 清除半径文本
    if (this.circleComObj.textEntity) {
      this._dataSource.entities.remove(this.circleComObj.textEntity)
      this.circleComObj.textEntity = null
    }
    if (this.circleComObj.circleEntity) {
      this._dataSource.entities.remove(this.circleComObj.circleEntity)
      this.circleComObj.circleEntity = null
    }

    // 重置store状态
    if (this.store) {
      this.store.commit('SET_SHOW_POINT', false)
      this.store.commit('SET_CURRENT_POINT', {})
      this.store.commit('SET_SHOW_POLYLINE', false)
      this.store.commit('SET_CURRENT_POLYLINE', {})
      this.store.commit('SET_SHOW_POLYGON', false)
      this.store.commit('SET_CURRENT_POLYGON', {})
      this.store.commit('SET_SHOW_CIRCLE', false)
      this.store.commit('SET_CURRENT_CIRCLE', {})
      this.store.commit('SET_SELECTED_DRAWING', null)
    }

    this._selectedDrawing = null
  }

  getDataSourceEntity (id: string): Cesium.Entity | null {
    const entity = this._dataSource.entities.getById(id)
    return entity || null
  }

  /**
   * 计算折线长度
   */
  private _calculatePolylineLength (positions: Cesium.Cartesian3[]): number {
    if (!positions || positions.length < 2) return 0

    let length = 0
    for (let i = 1; i < positions.length; i++) {
      length += Cesium.Cartesian3.distance(positions[i - 1], positions[i])
    }

    return length
  }

  /**
   * 计算多边形面积
   */
  private _calculatePolygonArea (positions: Cesium.Cartesian3[]): number {
    if (!positions || positions.length < 3) return 0

    let area = 0
    const length = positions.length

    for (let i = 0; i < length; i++) {
      const j = (i + 1) % length
      const point1 = positions[i]
      const point2 = positions[j]

      area += point1.x * point2.y - point1.y * point2.x
    }

    return Math.abs(area / 2)
  }

  /**
   * 计算高度差
   */
  private _calculateHeightDiff (positions: Cesium.Cartesian3[]): number {
    if (positions.length === 0) return 0

    let minHeight = Number.MAX_VALUE
    let maxHeight = Number.MIN_VALUE

    positions.forEach(position => {
      const cartographic = Cesium.Cartographic.fromCartesian(position)
      if (cartographic.height < minHeight) minHeight = cartographic.height
      if (cartographic.height > maxHeight) maxHeight = cartographic.height
    })

    return maxHeight - minHeight
  }

  /**
   * 隐藏信息面板 - 已移除
   */
  private _hideInfoPanel (): void {
    // 这个方法已不再需要
  }

  /**
   * 清除悬停高亮
   */
  private _clearHoverHighlight (): void {
    if (this._hoveredDrawing) {
      if (!this._selectedDrawing || this._hoveredDrawing.id !== this._selectedDrawing.id) {
        this._restoreDrawingStyle(this._hoveredDrawing)
      }
      this._hoveredDrawing = null
    }
  }

  /**
   * 恢复图形的原始样式
   */
  private _restoreDrawingStyle (drawing: any): void {
    const entity = drawing.entity
    let style

    if (this._selectedDrawing && drawing.id === this._selectedDrawing.id) {
      style = this._styles.selected
    } else {
      style = this._styles
    }

    if (drawing.isFromAPI) {
      this._restoreAPIDrawingStyle(drawing, entity, style)
      return
    }

    switch (drawing.type) {
      case 'Point':
        if (entity.point) {
          entity.point.color = style.point.color
          entity.point.pixelSize = style.point.pixelSize
          entity.point.outlineColor = style.point.outlineColor
          entity.point.outlineWidth = style.point.outlineWidth
        }
        break

      case 'Polyline':
        if (entity.polyline) {
          entity.polyline.width = style.polyline.width
          if (drawing.originalColor) {
            entity.polyline.material = Cesium.Color.fromCssColorString(drawing.originalColor)
          } else {
            entity.polyline.material = style.polyline.material
          }
        }
        break

      case 'Polygon':
        if (entity.polygon) {
          if (drawing.originalColor) {
            const color = Cesium.Color.fromCssColorString(drawing.originalColor)
            entity.polygon.material = color.withAlpha(0.5)
          } else {
            entity.polygon.material = style.polygon.material
          }
          if (entity.polygon.outline) {
            entity.polygon.outlineColor = style.polygon.outlineColor
          }
        }
        if (entity.polyline) {
          entity.polyline.width = style.polyline.width
          if (drawing.originalColor) {
            entity.polyline.material = Cesium.Color.fromCssColorString(drawing.originalColor)
          } else {
            entity.polyline.material = style.polyline.material
          }
        }
        break
      case 'Circle':
        if (entity.polyline) {
          entity.polyline.width = style.polyline.width
          if (drawing.originalColor) {
            entity.polyline.material = Cesium.Color.fromCssColorString(drawing.originalColor)
          } else {
            entity.polyline.material = style.polyline.material
          }
        }
        break
    }
  }

  /**
   * 恢复API图形的原始样式
   */
  private _restoreAPIDrawingStyle (drawing: any, entity: Cesium.Entity, style: any): void {
    const color = drawing.attributes?.color || '#2d8cf0'

    switch (drawing.type) {
      case 'Point':
        if (entity.point) {
          entity.point.color = Cesium.Color.fromCssColorString(color)
          entity.point.pixelSize = style.point.pixelSize
          entity.point.outlineColor = Cesium.Color.fromCssColorString(color)
          entity.point.outlineWidth = style.point.outlineWidth
        }
        break

      case 'Polyline':
        if (entity.polyline) {
          entity.polyline.width = style.polyline.width
          entity.polyline.material = Cesium.Color.fromCssColorString(color)
        }
        break

      case 'Polygon':
        if (entity.polygon) {
          const polygonColor = Cesium.Color.fromCssColorString(color)
          entity.polygon.material = polygonColor.withAlpha(0.5)
          if (entity.polygon.outline) {
            entity.polygon.outlineColor = polygonColor
          }
        }
        break

      case 'Circle': {
        const circleColor = Cesium.Color.fromCssColorString(color)
        if (entity.polygon) {
          entity.polygon.material = circleColor.withAlpha(0.5)
        }
        if (entity.polyline) {
          entity.polyline.material = circleColor
        }

        break
      }
    }
  }

  /**
   * 高亮显示图形
   */
  private _highlightDrawing (drawing: any, highlight: boolean, type: 'select' | 'hover' = 'select'): void {
    const entity = drawing.entity
    let style

    if (type === 'select') {
      style = highlight ? this._styles.selected : this._styles
    } else {
      style = highlight ? this._styles.hover : this._styles
    }

    switch (drawing.type) {
      case 'Point':
        if (entity.point) {
          if (highlight) {
            entity.point.outlineColor = style.point.outlineColor
            entity.point.outlineWidth = style.point.outlineWidth
            if (type === 'select') this.viewer.flyTo(entity)
          } else {
            this._restoreDrawingStyle(drawing)
          }
        }
        break

      case 'Polyline':
        if (entity.polyline) {
          if (highlight) {
            entity.polyline.width = style.polyline.width
            entity.polyline.material = new Cesium.PolylineOutlineMaterialProperty({
              color: Cesium.Color.fromCssColorString(drawing.originalColor || '#2d8cf0'),
              outlineColor: Cesium.Color.WHITE,
              outlineWidth: 3
            })
            entity.polyline.outlineWidth = style.polyline.outlineWidth
            entity.polyline.outlineColor = style.polyline.outlineColor
            if (type === 'select') this.viewer.flyTo(entity)
          } else {
            this._restoreDrawingStyle(drawing)
          }
        }
        break

      case 'Polygon':
        if (entity.polygon) {
          if (highlight) {
            entity.polyline.material = new Cesium.PolylineOutlineMaterialProperty({
              color: Cesium.Color.fromCssColorString(drawing.originalColor || '#2d8cf0'),
              outlineColor: Cesium.Color.WHITE,
              outlineWidth: 3
            })
            if (type === 'select') {
              const positions = drawing.latlngList.map(item => [item.longitude, item.latitude, item.height])
              entityFlyToBoundingSphere(positions, this.viewer, 'Polygon')
            }
          } else {
            this._restoreDrawingStyle(drawing)
          }
        }
        break
      case 'Circle':
        if (highlight) {
          entity.polyline.material = new Cesium.PolylineOutlineMaterialProperty({
            color: Cesium.Color.fromCssColorString(drawing.originalColor || '#2d8cf0'),
            outlineColor: Cesium.Color.WHITE,
            outlineWidth: 3
          })
          if (type === 'select') {
            const positions = drawing.latlngList.map(item => [item.longitude, item.latitude, item.height])
            entityFlyToBoundingSphere(positions[0], this.viewer, 'Circle', drawing.apiGraphic.radius)
          }
        } else {
          this._restoreDrawingStyle(drawing)
        }
    }
  }

  /**
   * 删除选中的图形
   */
  deleteSelectedDrawing (): void {
    if (!this._selectedDrawing) return

    const drawing = this._selectedDrawing
    this.deselectDrawing()
    this.deleteDrawing(drawing.entity)
  }

  /**
   * 激活点线面绘制
   */
  activate (drawType: string, continuous: boolean = false): void {
    this._saveOriginalCameraSettings()
    this.deselectDrawing()
    this._clearDraggablePoints()
    this._clearVirtualPoints()
    this.clearCurrentDrawing()

    this._drawType = drawType
    this._isContinuousDrawing = continuous
    this._isSelectionEnabled = false
    this._currentInteractionMode = 'draw'
    this._registerEvents()
    this._isDrawing = true
  }

  /**
   * 停止绘制
   */
  stopDrawing (): void {
    this.clearCurrentDrawing()
    this._isSelectionEnabled = true
    this._isContinuousDrawing = false
    this._currentInteractionMode = 'select'
    this._restoreCameraSettings()
  }

  /**
   * 注册鼠标事件
   */
  private _registerEvents (): void {
    this._removeDrawEvents()

    this._drawHandler = new Cesium.ScreenSpaceEventHandler(this.viewer.scene.canvas)

    switch (this._drawType) {
      case 'Point':
        this._leftClickEventForPoint()
        break
      case 'Polyline':
        this._leftClickEventForPolyline()
        this._mouseMoveEventForPolyline()
        break
      case 'Polygon':
        this._leftClickEventForPolygon()
        this._mouseMoveEventForPolygon()
        this._doubleClickEventForPolygon()
        break
      case 'PointLine':
        this._leftClickEventForPointLine()
        break
    }
  }

  /**
   * 继续绘制点线
   */
  drawDrawPointLine (): void {
    this._removeDrawEvents()
    this._drawType = 'PointLine'
    this._isContinuousDrawing = true
    this._isSelectionEnabled = false
    this._isDrawing = true
    this._currentInteractionMode = 'draw'
    this._drawHandler = new Cesium.ScreenSpaceEventHandler(this.viewer.scene.canvas)
    this._leftClickEventForPointLine()
  }

  drawPolygon (polygonList, list, startPoint) {
  // 取消当前选择和高亮
    if (this._selectedDrawing) this.deselectDrawing()

    // 清除可拖动点和虚拟点
    this._clearDraggablePoints()
    this._clearVirtualPoints()

    this.clearCurrentDrawing()
    this.clearPointLine()
    // 重置起始点状态
    this._hasStartingPoint = false
    this._startingPoint = null
    const pointsList: any[] = []
    if (startPoint) {
      let height = (startPoint.altitude || startPoint.height || 0) * 1
      if (this.store.state.showPointCloud || this.store.state.showModelTileset) {
        height += 100
      }
      this._hasStartingPoint = true
      this._startingPoint = Cartesian3.fromDegrees(startPoint.longitude * 1, startPoint.latitude * 1, height)
      // 创建起始点实体但不加入航点列表
      const startingPointEntity = this._dataSource.entities.add({
        position: this._startingPoint,
        billboard: {
          image: drone,
          clampToGround: true,
          width: 35,
          height: 35
        },
        // point: {
        //   color: Cesium.Color.GREEN, // 绿色表示起始点
        //   pixelSize: 15,
        //   outlineColor: Cesium.Color.WHITE,
        //   outlineWidth: 2,
        //   heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
        //   disableDepthTestDistance: Number.POSITIVE_INFINITY
        // }
      })
      // 存储起始点信息
      const pointInfo = {
        longitude: startPoint.longitude * 1,
        latitude: startPoint.latitude * 1,
        altitude: height || 0,
        id: 0, // 起始点ID为0
        isStartingPoint: true
      }
      // 添加到绘图列表但不加入航点列表
      const drawing = {
        id: `starting_point_${Date.now()}`,
        type: 'StartingPoint',
        entity: startingPointEntity,
        positions: [this._startingPoint],
        pointInfo
      }
      this._drawings.push(drawing)
    }
    if (polygonList.length > 0) {
      if (polygonList[0] !== polygonList[polygonList.length - 1]) {
        polygonList.push(polygonList[0])
      }
      const polygonArr = polygonList.map(item => {
        return Cartesian3.fromDegrees(item[0] * 1, item[1] * 1, item[2] * 1 || 0)
      })
      const polygonEntity = this._dataSource.entities.add({
        polygon: {
          hierarchy: polygonArr,
          ...this._styles.polygon,
          material: Cesium.Color.fromCssColorString('#2d8cf0').withAlpha(0.5)
        },
        polyline: {
          positions: polygonArr,
          ...this._styles.polyline,
          material: new Cesium.PolylineOutlineMaterialProperty({
            color: Cesium.Color.fromCssColorString('#2d8cf0'),
          }),
        }
      })
      const drawing = {
        id: `polygon_${Date.now()}`,
        type: 'PointPolygon',
        entity: polygonEntity,
        positions: polygonArr
      }
      this._drawings.push(drawing)
    }
    const pointArr = [this._startingPoint]
    if (list.length > 0) {
      list.forEach((e) => {
        pointsList.push(Cartesian3.fromDegrees(e[0] * 1, e[1] * 1, e[2] * 1 || 0))
        pointArr.push(Cartesian3.fromDegrees(e[0] * 1, e[1] * 1, e[2] * 1 || 0))
      })
      const polyline = this._dataSource.entities.add({
        polyline: {
          positions: pointArr,
          ...polylineOption
        },
      }) as any
      const drawing = {
        id: `polyline_${Date.now()}`,
        type: 'PointLine',
        entity: polyline,
        positions: pointsList
      }
      this._drawings.push(drawing)
    }
  }

  /**
  * 在地图上绘制点线
  *
  * @param list 包含笛卡尔坐标点的数组
  */
  drawPointLine (list: Cesium.Cartesian3[], flag: boolean = false): void {
    // 取消当前选择和高亮
    this.deselectDrawing()

    // 清除可拖动点和虚拟点
    this._clearDraggablePoints()
    this._clearVirtualPoints()

    this.clearCurrentDrawing()
    this.clearPointLine()

    // 重置起始点状态
    this._hasStartingPoint = false
    this._startingPoint = null

    const pointsList: any[] = []
    console.log(list.length, 'length')

    list.forEach(async (e, i: number) => {
      console.log('draw', i)

      this._clickPosition = this.conversion(e)
      let { longitude, latitude, height } = this._clickPosition
      if (height <= 0) height = 200
      if (this.store.state.showPointCloud || this.store.state.showModelTileset) {
        height += 100
      }
      const position = { longitude, latitude, height }

      // 第一个点作为起始点
      if (i === 0) {
        this._hasStartingPoint = true
        this._startingPoint = Cartesian3.fromDegrees(longitude, latitude, height || 0)

        // 创建起始点实体但不加入航点列表
        const startingPointEntity = this._dataSource.entities.add({
          position: this._startingPoint,
          billboard: {
            image: drone,
            clampToGround: true,
            width: 35,
            height: 35
          },
          // point: {
          //   color: Cesium.Color.GREEN, // 绿色表示起始点
          //   pixelSize: 15,
          //   outlineColor: Cesium.Color.WHITE,
          //   outlineWidth: 2,
          //   heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
          //   disableDepthTestDistance: Number.POSITIVE_INFINITY
          // }
        })

        // 存储起始点信息
        const pointInfo = {
          longitude,
          latitude,
          height,
          id: 0, // 起始点ID为0
          isStartingPoint: true
        }

        // 添加到绘图列表但不加入航点列表
        const drawing = {
          id: `starting_point_${Date.now()}`,
          type: 'StartingPoint',
          entity: startingPointEntity,
          positions: [this._startingPoint],
          pointInfo
        }
        this._drawings.push(drawing)
      } else {
        // 新增航点（放大显示）
        await this.addEntity(position, true, pointsList)
        if (flag) {
          await this.addFrustum(position)
        }

        // 如果有起始点和至少一个航点，更新航线
        if (this._hasStartingPoint && pointsList.length >= 1) {
          // 删除旧的航线实体（如果存在）
          const oldDrawing = this._drawings.find(d => d.type === 'PointLine' && d.entity._isDrawing)
          if (oldDrawing) {
            this._dataSource.entities.remove(oldDrawing.entity)
            const index = this._drawings.indexOf(oldDrawing)
            if (index !== -1) {
              this._drawings.splice(index, 1)
            }
          }

          // 创建新的航线实体并选中（包含起始点和所有航点）
          this.addEntityLine(true, pointsList)
        }
      }
    })
  }

  /**
   * 绘制航点航线（放大显示点）
   */
  private _leftClickEventForPointLine (): void {
    this._drawHandler!.setInputAction(async (e: any) => {
      if (state.currentType === 'PointLine' || state.currentType === 'PolygonPoint') return

      const ray = this.viewer.camera.getPickRay(e.position)
      const cartesian = this.viewer.scene.globe.pick(ray, this.viewer.scene)
      if (!cartesian) return

      this._clickPosition = this.conversion(cartesian)
      let { longitude, latitude, height } = this._clickPosition
      if (height <= 0) height = 200

      const position = { longitude, latitude, height }

      // 检查是否与上一个点位置相同
      const isDuplicate = this.entitiesPointOptions.length > 0 &&
        this._isSamePoint(position, this.entitiesPointOptions[this.entitiesPointOptions.length - 1].pointInfo)

      if (!isDuplicate) {
        if (!this._hasStartingPoint) {
          await this.addStartingPoint(position)
          this._hasStartingPoint = true
          this._startingPoint = Cartesian3.fromDegrees(longitude, latitude, height)
        } else {
          await this.addEntity(position, true)
          await this.addFrustum(position)

          if (this._hasStartingPoint && this.entitiesPointOptions.length >= 1) {
            this.addEntityLine(true)
          }
        }

        this.store.commit('SET_ENTITIES_POINT_OPTION', this.entitiesPointOptions)
        if (this.entitiesPointOptions.length > 0) {
          this.store.commit('SET_CURRPOINT_INFO',
            this.entitiesPointOptions[this.entitiesPointOptions.length - 1]
          )
        }
      }
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK)
  }

  private async addStartingPoint (position: any): Promise<void> {
    const { longitude, latitude, height } = position
    let terrainHeight = 0
    try {
      const cartographic = Cesium.Cartographic.fromDegrees(longitude, latitude)
      terrainHeight = this.viewer.scene.globe.getHeight(cartographic) || 0
    } catch (e) {
      console.warn('获取起始点地形高度失败')
    }

    const finalHeight = Math.max(height, terrainHeight + 10)

    const startingPointEntity = this._dataSource.entities.add({
      position: Cartesian3.fromDegrees(longitude, latitude, finalHeight),
      polyline: {
        positions: new CallbackProperty(() => {
          return [
            Cartesian3.fromDegrees(longitude, latitude, terrainHeight),
            Cartesian3.fromDegrees(longitude, latitude, finalHeight),
          ]
        }, false),
        material: new StripeMaterialProperty({ ...stripeMaterialPropertyOption }),
        width: 5,
        clampToGround: false,
      },
      billboard: {
        image: drone,
        clampToGround: false,
        width: 35,
        height: 35,
        heightReference: Cesium.HeightReference.NONE,
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
      },
    })

    const pointInfo = {
      longitude,
      latitude,
      height,
      id: 0,
      isStartingPoint: true
    }

    const drawing = {
      id: `starting_point_${Date.now()}`,
      type: 'StartingPoint',
      entity: startingPointEntity,
      positions: [Cartesian3.fromDegrees(longitude, latitude, height)],
      pointInfo
    }
    this._drawings.push(drawing)
  }

  // 检查两个点是否相同
  private _isSamePoint (point1: any, point2: any, tolerance: number = 0.0001): boolean {
    if (!point1 || !point2) return false

    const latDiff = Math.abs(point1.latitude - point2.latitude)
    const lonDiff = Math.abs(point1.longitude - point2.longitude)
    const heightDiff = Math.abs(point1.height - point2.height)

    return latDiff < tolerance && lonDiff < tolerance && heightDiff < tolerance
  }

  /**
   * 通过实体查找绘图对象
   */
  private _findDrawingByEntity (entity: any): any {
    if (entity.id) {
      const byId = this._drawings.find(d => d.entity && d.entity.id === entity.id.id)
      if (byId) return byId
    }

    if (entity.id) {
      const byId = this._drawings.find(d => d.entity && d.entity.id === entity.id)
      if (byId) return byId
    }

    if (entity._drawingId) {
      const byDrawingId = this._drawings.find(d => d.id === entity._drawingId)
      if (byDrawingId) return byDrawingId
    }

    if (entity.position) {
      const entityPos = entity.position.getValue(Cesium.JulianDate.now())
      if (entityPos) {
        return this._drawings.find(d => {
          if (!d.entity || !d.entity.position) return false
          const drawingPos = d.entity.position.getValue(Cesium.JulianDate.now())
          if (!drawingPos) return false
          return Cesium.Cartesian3.equals(entityPos, drawingPos)
        })
      }
    }

    return this._drawings.find(d => d.entity === entity)
  }

  /**
   * 鼠标事件之绘制点的左击事件
   */
  private _leftClickEventForPoint (): void {
    this._drawHandler!.setInputAction((e: any) => {
      if (state.currentType === 'PointLine' || state.currentType === 'PolygonPoint') return

      if (!this._isSelectionEnabled) {
        const ray = this.viewer.camera.getPickRay(e.position)
        if (!ray) return

        const ellipsoid = this.viewer.scene.globe.ellipsoid
        let p = this.viewer.scene.camera.pickEllipsoid(e.position, ellipsoid)

        if (!p || !this._isValidPosition(p)) {
          const cameraPosition = this.viewer.camera.position
          const direction = this.viewer.camera.direction
          const distance = 1000

          p = new Cesium.Cartesian3()
          Cesium.Cartesian3.multiplyByScalar(direction, distance, p)
          Cesium.Cartesian3.add(cameraPosition, p, p)
        }

        const positions = this.conversion(p)
        const pointPosition = [positions.longitude, positions.latitude, positions.height]

        // 创建可序列化的信息对象
        const info = {
          color: '#2d8cf0',
          position: [positions.longitude, positions.latitude, positions.height],
          type: 'Point',
          timestamp: Date.now()
        }

        // console.log(info, 'elementMarkInfo==>point')

        // 序列化后发送
        try {
          const serializableInfo = JSON.parse(JSON.stringify(info))
          emitter.emit('elementMarkInfo', serializableInfo)
        } catch (error) {
          console.error('点数据序列化失败:', error)
          emitter.emit('elementMarkInfo', {
            error: '数据序列化失败',
            type: 'Point'
          })
        }

        // 如果不是连续绘制模式，完成绘制
        if (!this._isContinuousDrawing) {
          this._isDrawing = false
          this._isSelectionEnabled = true
        }
      }
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK)
  }

  /**
   * 初始化多边形实体
   */
  private initPolygonEntity (): void {
    const color = Cesium.Color.fromCssColorString(this.polygonAttr.color)
    color.alpha = 0.5

    this.polygonComObj.polygonEntity = this._dataSource.entities.add({
      polygon: {
        hierarchy: new Cesium.PolygonHierarchy([]),
        material: color,
        classificationType: Cesium.ClassificationType.BOTH
      },
      polyline: {
        positions: [],
        width: 6,
        material: new Cesium.PolylineOutlineMaterialProperty({
          color: Cesium.Color.fromCssColorString(this.polygonAttr.color),
          outlineColor: new Cesium.Color(1, 1, 1, 1),
          outlineWidth: 3
        }),
        clampToGround: true,
        classificationType: Cesium.ClassificationType.BOTH
      },
      show: true
    }) as PolygonEntity

    this.polygonComObj.polygonEntity.attrColor = this.polygonAttr.color

    this.polygonComObj.pointTextEntity1 = this._dataSource.entities.add({
      parent: this.polygonComObj.polygonEntity,
      position: undefined,
      label: {
        font: '16px 黑体',
        fillColor: new Cesium.Color(1, 1, 1, 1),
        outlineColor: Cesium.Color.BLACK,
        outlineWidth: 4,
        showBackground: true,
        horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
        verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
        pixelOffset: new Cesium.Cartesian2(0, -15),
        text: '',
        scaleByDistance: new Cesium.NearFarScalar(1000, 1, 500000, 0.8),
        disableDepthTestDistance: 500000,
        scale: 1,
        style: 2
      }
    })

    this.polygonComObj.pointTextEntity2 = this._dataSource.entities.add({
      parent: this.polygonComObj.polygonEntity,
      position: undefined,
      label: {
        font: '16px 黑体',
        fillColor: new Cesium.Color(1, 1, 1, 1),
        outlineColor: Cesium.Color.BLACK,
        outlineWidth: 4,
        showBackground: true,
        horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
        verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
        pixelOffset: new Cesium.Cartesian2(0, -15),
        text: '',
        scaleByDistance: new Cesium.NearFarScalar(1000, 1, 500000, 0.8),
        disableDepthTestDistance: 500000,
        scale: 1,
        style: 2
      }
    })
  }

  /**
   * 初始化线实体
   */
  private initPolylineEntity (): void {
    this.polylineComObj.polylineEntity = this._dataSource.entities.add({
      polyline: {
        positions: [],
        width: 6,
        material: new Cesium.PolylineOutlineMaterialProperty({
          color: Cesium.Color.fromCssColorString(this.polylineAttr.color),
          outlineColor: new Cesium.Color(1, 1, 1, 1),
          outlineWidth: 3
        }),
        clampToGround: true,
        classificationType: Cesium.ClassificationType.BOTH
      },
      show: true
    }) as PolylineEntity

    this.polylineComObj.polylineEntity.attrColor = this.polylineAttr.color

    this.polylineComObj.pointTextEntity = this._dataSource.entities.add({
      parent: this.polylineComObj.polylineEntity,
      position: undefined,
      label: {
        font: '16px 黑体',
        fillColor: new Cesium.Color(1, 1, 1, 1),
        outlineColor: Cesium.Color.BLACK,
        outlineWidth: 4,
        showBackground: true,
        horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
        verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
        pixelOffset: new Cesium.Cartesian2(0, -15),
        text: '',
        scaleByDistance: new Cesium.NearFarScalar(1000, 1, 500000, 0.8),
        disableDepthTestDistance: 500000,
        scale: 1,
        style: 2
      }
    })
  }

  /**
   * 计算距离
   */
  private calcDis (position1: Cesium.Cartesian3, position2: Cesium.Cartesian3): string {
    const distance = Cesium.Cartesian3.distance(position1, position2)
    let formattedDistance = distance.toFixed(1)

    if (distance > 10000) {
      formattedDistance = `${(distance / 1000).toFixed(1)}km`
    } else {
      formattedDistance = `${formattedDistance}m`
    }

    return formattedDistance
  }

  /**
   * 计算中心位置
   */
  private calcCenterPosition (position1: Cesium.Cartesian3, position2: Cesium.Cartesian3): Cesium.Cartesian3 {
    return new Cesium.Cartesian3(
      (position1.x + position2.x) / 2,
      (position1.y + position2.y) / 2,
      (position1.z + position2.z) / 2
    )
  }

  /**
   * 计算线长度
   */
  private calcPolylineLength (positions: Cesium.Cartesian3[]): number {
    let length = 0
    for (let i = 0; i < positions.length - 1; i++) {
      length += Cesium.Cartesian3.distance(positions[i], positions[i + 1])
    }
    return length
  }

  /**
   * 鼠标事件之绘制线的左击事件
   */
  private _leftClickEventForPolyline (): void {
    this._drawHandler!.setInputAction((e: any) => {
      if (state.currentType === 'PointLine' || state.currentType === 'PolygonPoint') return

      if (!this._isSelectionEnabled) {
        const ray = this.viewer.camera.getPickRay(e.position)
        if (!ray) return

        const ellipsoid = this.viewer.scene.globe.ellipsoid
        let p = this.viewer.scene.camera.pickEllipsoid(e.position, ellipsoid)

        if (!p || !this._isValidPosition(p)) {
          const cameraPosition = this.viewer.camera.position
          const direction = this.viewer.camera.direction
          const distance = 1000

          p = new Cesium.Cartesian3()
          Cesium.Cartesian3.multiplyByScalar(direction, distance, p)
          Cesium.Cartesian3.add(cameraPosition, p, p)
        }

        const pickedObject = this.viewer.scene.pick(e.position)
        if (pickedObject && pickedObject.id === this.polylineComObj.pointEntityList[this.polylineComObj.pointEntityList.length - 1] && this.polylineComObj.positionList.length > 1) {
          this._doubleClickEventForPolyline()
          return
        }

        // 检查是否与上一个点位置相同
        const isDuplicate = this._tempPositions.length > 0 &&
          this._isSamePosition(p, this._tempPositions[this._tempPositions.length - 1])

        if (!isDuplicate) {
          if (this.polylineComObj.positionList.length === 0) {
            this.initPolylineEntity()
          }

          this._tempPositions.push(p)
          this.polylineComObj.positionList.push(p)
          this.polylineComObj.movePositions = [...this.polylineComObj.positionList]

          if (!(this.polylineComObj.polylineEntity!.polyline!.positions instanceof Cesium.CallbackProperty)) {
            this.polylineComObj.polylineEntity!.polyline!.positions = new Cesium.CallbackProperty(
              () => this.polylineComObj.positionList,
              false
            )
          }

          const point = this.addPoint(
            p,
            15,
            new Cesium.Color(1, 1, 1, 1),
            this.polylineComObj.positionList.length,
            'polyline_new_point',
            this.polylineComObj.polylineEntity!
          ) as PointEntity

          this.polylineComObj.pointEntityList.push(point)

          if (this.polylineComObj.pointEntityList.length > 1) {
            this.polylineComObj.pointEntityList[this.polylineComObj.pointEntityList.length - 1].point!.show = true
            this.polylineComObj.pointEntityList[this.polylineComObj.pointEntityList.length - 1].billboard!.show = true
            this.polylineComObj.pointEntityList[this.polylineComObj.pointEntityList.length - 2].billboard!.show = false
          }

          if (this.polylineComObj.positionList.length > 1) {
            const lastPosition = this.polylineComObj.positionList[this.polylineComObj.positionList.length - 1]
            const secondLastPosition = this.polylineComObj.positionList[this.polylineComObj.positionList.length - 2]
            const distance = this.calcDis(lastPosition, secondLastPosition)
            const centerPosition = this.calcCenterPosition(lastPosition, secondLastPosition)
            const text = this.addText(centerPosition, distance)

            text.parent = this.polylineComObj.polylineEntity
            this.polylineComObj.textEntityList.push(text)
          }
        }
      }
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK)
  }

  // 检查两个位置是否相同
  private _isSamePosition (pos1: Cesium.Cartesian3, pos2: Cesium.Cartesian3, tolerance: number = 0.1): boolean {
    return Cesium.Cartesian3.distance(pos1, pos2) < tolerance
  }

  /**
   * 鼠标事件之绘制线的移动事件
   */
  private _mouseMoveEventForPolyline (): void {
    this._drawHandler!.setInputAction((e: any) => {
      if (state.currentType === 'PointLine' || state.currentType === 'PolygonPoint') return

      if (!this._isDrawing || this._tempPositions.length === 0) return

      const ray = this.viewer.camera.getPickRay(e.endPosition)
      if (!ray) return

      const ellipsoid = this.viewer.scene.globe.ellipsoid
      let p = this.viewer.scene.camera.pickEllipsoid(e.endPosition, ellipsoid)

      if (!p || !this._isValidPosition(p)) {
        const cameraPosition = this.viewer.camera.position
        const direction = this.viewer.camera.direction
        const distance = 1000

        p = new Cesium.Cartesian3()
        Cesium.Cartesian3.multiplyByScalar(direction, distance, p)
        Cesium.Cartesian3.add(cameraPosition, p, p)
      }

      if (this.polylineComObj.positionList.length > 0 && e.endPosition) {
        const precisePosition = p

        this.polylineComObj.polylineEntity!.polyline!.positions = new Cesium.CallbackProperty(() => {
          if (this.polylineComObj.movePositions.length === this.polylineComObj.positionList.length + 1) {
            this.polylineComObj.movePositions.pop()
          }
          this.polylineComObj.movePositions.push(precisePosition)
          return this.polylineComObj.movePositions
        }, false)

        const lastFixedPosition = this.polylineComObj.positionList[this.polylineComObj.positionList.length - 1]
        const distance = this.calcDis(lastFixedPosition, precisePosition)

        if (this.polylineComObj.pointTextEntity) {
          this.polylineComObj.pointTextEntity.position = precisePosition
          this.polylineComObj.pointTextEntity.label!.text = distance
        }
      }
      this._mousePos = p
    }, Cesium.ScreenSpaceEventType.MOUSE_MOVE)
  }

  /**
   * 添加文本
   */
  private addText (position: Cesium.Cartesian3, text: string): Cesium.Entity {
    return this._dataSource.entities.add({
      position: position,
      label: {
        font: '16px 黑体',
        fillColor: new Cesium.Color(1, 1, 1, 1),
        outlineColor: Cesium.Color.BLACK,
        outlineWidth: 4,
        showBackground: true,
        horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
        verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
        pixelOffset: new Cesium.Cartesian2(0, -5),
        text: text,
        scaleByDistance: new Cesium.NearFarScalar(1000, 1, 500000, 0.8),
        disableDepthTestDistance: 500000,
        scale: 1,
        style: 2
      }
    })
  }

  /**
   * 鼠标事件之绘制线的双击事件
   */
  private _doubleClickEventForPolyline (): any {
    if (this.polylineComObj.positionList.length > 1) {
      const color = this.polylineComObj.polylineEntity!.attrColor!
      const latlngList = this.conversion(this.polylineComObj.positionList)

      // 创建可序列化的信息对象
      const info = {
        color: color,
        position: latlngList.map((el) => [el.longitude, el.latitude, el.height]),
        type: 'Polyline',
        timestamp: Date.now()
      }

      // console.log(info, 'elementMarkInfo==>polyline')

      // 序列化后发送
      try {
        const serializableInfo = JSON.parse(JSON.stringify(info))
        emitter.emit('elementMarkInfo', serializableInfo)
      } catch (error) {
        console.error('折线数据序列化失败:', error)
        emitter.emit('elementMarkInfo', {
          error: '数据序列化失败',
          type: 'Polyline'
        })
      }

      this._dataSource.entities.remove(this.polylineComObj.polylineEntity!)
      this._dataSource.entities.remove(this.polylineComObj.pointTextEntity!)

      this.polylineComObj.pointEntityList.forEach((pointEntity) => {
        this._dataSource.entities.remove(pointEntity)
      })
      this.polylineComObj.textEntityList.forEach((pointEntity) => {
        this._dataSource.entities.remove(pointEntity)
      })
      this.polylineComObj.editPointEntityList.forEach((pointEntity) => {
        this._dataSource.entities.remove(pointEntity)
      })

      this.clearCurrentDrawing()
      this.polylineComObj.positionList = []
      this.polylineComObj.movePositions = []
      this.polylineComObj.polylineEntity = null
      this.polylineComObj.pointTextEntity = null
      this.polylineComObj.pointEntityList = []
      this.polylineComObj.textEntityList = []
      this.polylineComObj.editPointEntityList = []

      return info
    }
    return null
  }

  /**
   * 添加点
   */
  private addPoint (
    position: Cesium.Cartesian3,
    pixelSize: number,
    color: Cesium.Color,
    pointIndex: number,
    typeInfo: string,
    parent: Cesium.Entity
  ): PointEntity {
    const entity = this._dataSource.entities.add({
      position: position,
      point: {
        pixelSize: pixelSize,
        color: color,
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
        heightReference: Cesium.HeightReference.NONE,
        outlineColor: Cesium.Color.WHITE,
        outlineWidth: 2,
        show: true
      },
      billboard: {
        image: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAYAQMAAADJbu9PAAAAAXNSR0IB2cksfwAAAAlwSFlzAAALEwAACxMBAJqcGAAAAAZQTFRFAAAAW/tlDx7VPwAAAAJ0Uk5TAP9bkSK1AAAAJElEQVR4nGNkrGdk3svIfpOR/yGj/E1G+32M9fWM/xlAgqQgALk7Exo5KkuiAAAAAElFTkSuQmCC',
        show: false,
        scale: 0.35,
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
        heightReference: Cesium.HeightReference.NONE
      }
    }) as PointEntity

    entity.pointIndex = pointIndex
    entity.typeInfo = typeInfo
    entity.parent = parent

    return entity
  }

  /**
   * 计算多边形面积
   */
  private calcPolygonArea (positions: Cesium.Cartesian3[]): number {
    let area = 0
    const length = positions.length

    if (length < 3) return 0

    const cartographics = positions.map(pos => Cesium.Cartographic.fromCartesian(pos))

    for (let i = 0; i < length; i++) {
      const j = (i + 1) % length
      const k = (i + 2) % length

      const lon1 = Cesium.Math.toDegrees(cartographics[i].longitude)
      const lat1 = Cesium.Math.toDegrees(cartographics[i].latitude)
      const lon2 = Cesium.Math.toDegrees(cartographics[j].longitude)
      const lat2 = Cesium.Math.toDegrees(cartographics[j].latitude)
      const lon3 = Cesium.Math.toDegrees(cartographics[k].longitude)
      const lat3 = Cesium.Math.toDegrees(cartographics[k].latitude)

      const a = this.calcGreatCircleDistance(lat1, lon1, lat2, lon2)
      const b = this.calcGreatCircleDistance(lat2, lon2, lat3, lon3)
      const c = this.calcGreatCircleDistance(lat3, lon3, lat1, lon1)

      const s = (a + b + c) / 2
      const triangleArea = Math.sqrt(s * (s - a) * (s - b) * (s - c))

      area += triangleArea
    }

    return area
  }

  /**
   * 计算大圆距离
   */
  private calcGreatCircleDistance (lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371000
    const φ1 = lat1 * Math.PI / 180
    const φ2 = lat2 * Math.PI / 180
    const Δφ = (lat2 - lat1) * Math.PI / 180
    const Δλ = (lon2 - lon1) * Math.PI / 180

    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) *
      Math.sin(Δλ / 2) * Math.sin(Δλ / 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

    return R * c
  }

  /**
   * 计算高度差
   */
  private calcHeightDiff (positions: Cesium.Cartesian3[]): number {
    if (positions.length === 0) return 0

    let minHeight = Number.MAX_VALUE
    let maxHeight = Number.MIN_VALUE

    positions.forEach(position => {
      const cartographic = Cesium.Cartographic.fromCartesian(position)
      if (cartographic.height < minHeight) minHeight = cartographic.height
      if (cartographic.height > maxHeight) maxHeight = cartographic.height
    })

    return maxHeight - minHeight
  }

  /**
   * 鼠标事件之绘制面的左击事件
   */
  private _leftClickEventForPolygon (): void {
    this._drawHandler!.setInputAction((e: any) => {
      if (state.currentType === 'PointLine' || state.currentType === 'PolygonPoint') return

      const ray = this.viewer.camera.getPickRay(e.position)
      if (!ray) return

      const ellipsoid = this.viewer.scene.globe.ellipsoid
      let p = this.viewer.scene.camera.pickEllipsoid(e.position, ellipsoid)

      if (!p || !this._isValidPosition(p)) {
        const cameraPosition = this.viewer.camera.position
        const direction = this.viewer.camera.direction
        const distance = 1000

        p = new Cesium.Cartesian3()
        Cesium.Cartesian3.multiplyByScalar(direction, distance, p)
        Cesium.Cartesian3.add(cameraPosition, p, p)
      }

      const pickedObject = this.viewer.scene.pick(e.position)
      // if (pickedObject && pickedObject.id === this.polygonComObj.pointEntityList[this.polygonComObj.pointEntityList.length - 1] && this.polygonComObj.positionList.length > 1) {
      //   this._doubleClickEventForPolygon()
      //   return
      // }

      this._tempPositions.push(p)

      if (!this.polygonComObj.polygonEntity) {
        this.initPolygonEntity()
      }

      this.polygonComObj.positionList.push(p)
      this.polygonComObj.copyPositionList.push(JSON.parse(JSON.stringify(p)))
      this.polygonComObj.movePositions = [...this.polygonComObj.positionList]

      const point = this.addPoint(
        p,
        15,
        new Cesium.Color(1, 1, 1, 1),
        this.polygonComObj.positionList.length - 1,
        'polygon_new_point',
        this.polygonComObj.polygonEntity!
      ) as PointEntity

      this.polygonComObj.pointEntityList.push(point)

      if (this.polygonComObj.pointEntityList.length > 2) {
        this.polygonComObj.pointEntityList[this.polygonComObj.pointEntityList.length - 1].point!.show = false
        this.polygonComObj.pointEntityList[this.polygonComObj.pointEntityList.length - 1].billboard!.show = true
        this.polygonComObj.pointEntityList[this.polygonComObj.pointEntityList.length - 2].point!.show = true
        this.polygonComObj.pointEntityList[this.polygonComObj.pointEntityList.length - 2].billboard!.show = false
      }

      this.polygonComObj.polygonEntity!.polygon!.hierarchy = [...this.polygonComObj.positionList]

      if (this.polygonComObj.positionList.length > 1) {
        this.polygonComObj.polygonEntity!.polyline!.positions = [...this.polygonComObj.positionList, this.polygonComObj.positionList[0]]

        const lastPosition = this.polygonComObj.positionList[this.polygonComObj.positionList.length - 1]
        const secondLastPosition = this.polygonComObj.positionList[this.polygonComObj.positionList.length - 2]
        const distance = this.calcDis(lastPosition, secondLastPosition)
        const centerPosition = this.calcCenterPosition(lastPosition, secondLastPosition)
        const text = this.addText(centerPosition, distance)

        text.parent = this.polygonComObj.polygonEntity
        this.polygonComObj.textEntityList.push(text)
      }
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK)
  }

  /**
   * 鼠标事件之绘制面的移动事件
   */
  private _mouseMoveEventForPolygon (): void {
    this._drawHandler!.setInputAction((e: any) => {
      if (state.currentType === 'PointLine' || state.currentType === 'PolygonPoint') return

      if (!this._isDrawing || this._tempPositions.length === 0) return

      const ray = this.viewer.camera.getPickRay(e.endPosition)
      if (!ray) return

      const ellipsoid = this.viewer.scene.globe.ellipsoid
      let position = this.viewer.scene.camera.pickEllipsoid(e.endPosition, ellipsoid)

      if (!position || !this._isValidPosition(position)) {
        const cameraPosition = this.viewer.camera.position
        const direction = this.viewer.camera.direction
        const distance = 1000

        position = new Cesium.Cartesian3()
        Cesium.Cartesian3.multiplyByScalar(direction, distance, position)
        Cesium.Cartesian3.add(cameraPosition, position, position)
      }

      if (this.polygonComObj.positionList.length > 0 && position) {
        this.polygonComObj.polygonEntity!.polygon!.hierarchy = new Cesium.CallbackProperty(() => {
          if (this.polygonComObj.movePositions.length === this.polygonComObj.positionList.length + 1) {
            this.polygonComObj.movePositions.pop()
          }
          this.polygonComObj.movePositions.push(position!)
          return new Cesium.PolygonHierarchy(this.polygonComObj.movePositions)
        }, false)

        this.polygonComObj.polygonEntity!.polyline!.positions = new Cesium.CallbackProperty(() => {
          if (this.polygonComObj.movePositions.length === this.polygonComObj.positionList.length + 1) {
            this.polygonComObj.movePositions.pop()
          }
          this.polygonComObj.movePositions.push(position!)
          return [...this.polygonComObj.movePositions, this.polygonComObj.movePositions[0]]
        }, false)
      }

      if (this.polygonComObj.positionList.length > 1 && position) {
        const firstPosition = this.polygonComObj.positionList[0]
        const lastPosition = this.polygonComObj.positionList[this.polygonComObj.positionList.length - 1]

        const distance1 = this.calcDis(position, firstPosition)
        const distance2 = this.calcDis(position, lastPosition)

        const centerPosition1 = this.calcCenterPosition(position, firstPosition)
        const centerPosition2 = this.calcCenterPosition(position, lastPosition)

        this.polygonComObj.pointTextEntity1!.label!.text = distance1
        this.polygonComObj.pointTextEntity2!.label!.text = distance2

        this.polygonComObj.pointTextEntity1!.position = centerPosition1
        this.polygonComObj.pointTextEntity2!.position = centerPosition2
      }
    }, Cesium.ScreenSpaceEventType.MOUSE_MOVE)
  }

  /**
   * 鼠标事件之绘制面的双击事件
   */
  private _doubleClickEventForPolygon (): any {
    this._drawHandler!.setInputAction(() => {
      if (this.polygonComObj.positionList.length > 2) {
        const firstPosition = this.polygonComObj.positionList[0]
        const lastPosition = this.polygonComObj.positionList[this.polygonComObj.positionList.length - 1]
        const distance = this.calcDis(firstPosition, lastPosition)
        const centerPosition = this.calcCenterPosition(firstPosition, lastPosition)
        const text = this.addText(centerPosition, distance)

        text.parent = this.polygonComObj.polygonEntity
        this.polygonComObj.textEntityList.push(text)

        const color = this.polygonComObj.polygonEntity!.attrColor!

        this.editObj.currentPos = [...this.polygonComObj.positionList]
        this.editObj.currentEditType = 'polygon'
        this.editObj.editSwitch = true

      this.polygonComObj.polygonEntity!.polyline!.positions = [...this.polygonComObj.positionList, this.polygonComObj.positionList[0]]
      this.polygonComObj.polygonEntity!.polygon!.hierarchy = new Cesium.PolygonHierarchy([...this.polygonComObj.positionList])

      this.polygonComObj.pointEntityList[this.polygonComObj.pointEntityList.length - 1].point!.show = true
      this.polygonComObj.pointEntityList[this.polygonComObj.pointEntityList.length - 1].billboard!.show = false

      const polygonColor = Cesium.Color.fromCssColorString(color)
      polygonColor.alpha = 0.5

      const polygonEntity = this._dataSource.entities.add({
        polygon: {
          hierarchy: new Cesium.PolygonHierarchy([]),
          material: polygonColor,
          classificationType: Cesium.ClassificationType.BOTH
        },
        polyline: {
          positions: [],
          width: 4,
          material: Cesium.Color.fromCssColorString(color),
          clampToGround: true,
          classificationType: Cesium.ClassificationType.BOTH
        },
        show: true
      }) as PolygonEntity

      this.editObj.backPolygon = polygonEntity
      polygonEntity.typeInfo = 'polygon'
      polygonEntity.attrColor = color

      this.polygonComObj.copyPositionList.push(this.polygonComObj.copyPositionList[0])
      const latlngList = this.conversion(this.polygonComObj.copyPositionList)
      polygonEntity.latlngList = latlngList

      const horizontalDis = this.calcPolylineLength([...this.polygonComObj.positionList, this.polygonComObj.positionList[0]])
      const horizontalArea = this.calcPolygonArea(this.polygonComObj.positionList)
      const heightDiff = this.calcHeightDiff(this.polygonComObj.positionList)

      polygonEntity.attr = {
        horizontalDis: horizontalDis,
        horizontalArea: horizontalArea,
        heightDiff: heightDiff
      }

      const graphical: GraphicalEntity = {
        type: 'polygon',
        entity: polygonEntity,
        positions: [...this.polygonComObj.positionList],
        attributes: { color },
        latlngList: latlngList,
        attr: {
          horizontalDis: horizontalDis,
          horizontalArea: horizontalArea,
          heightDiff: heightDiff
        }
      }

      this.graphicalEntities.push(graphical)
      this.currentGraphical = graphical

      // 创建可序列化的信息对象
      const info = {
        color: color,
        position: [latlngList.map((el) => [el.longitude, el.latitude, el.height])],
        type: 'Polygon',
        timestamp: Date.now()
      }

      // console.log(info, 'elementMarkInfo==>polygon')

      // 序列化后发送
      try {
        const serializableInfo = JSON.parse(JSON.stringify(info))
        emitter.emit('elementMarkInfo', serializableInfo)
      } catch (error) {
        console.error('多边形数据序列化失败:', error)
        emitter.emit('elementMarkInfo', {
          error: '数据序列化失败',
          type: 'Polygon'
        })
      }

      this.clearCurrentDrawing()
      this._dataSource.entities.remove(this.polygonComObj.polygonEntity!)
      this._dataSource.entities.remove(this.polygonComObj.pointTextEntity1!)
      this._dataSource.entities.remove(this.polygonComObj.pointTextEntity2!)

      this.polygonComObj.pointEntityList.forEach(el => {
        this._dataSource.entities.remove(el)
      })
      this.polygonComObj.textEntityList.forEach(el => {
        this._dataSource.entities.remove(el)
      })
      this.polygonComObj.editPointEntityList.forEach(el => {
        this._dataSource.entities.remove(el)
      })
      this.polygonComObj.entityList.forEach(el => {
        this._dataSource.entities.remove(el)
      })

      this.polygonComObj.positionList = []
      this.polygonComObj.copyPositionList = []
      this.polygonComObj.movePositions = []
      this.polygonComObj.polygonEntity = null
      this.polygonComObj.pointTextEntity1 = null
      this.polygonComObj.pointTextEntity2 = null
      this.polygonComObj.pointEntityList = []
      this.polygonComObj.textEntityList = []
      this.polygonComObj.editPointEntityList = []
      this.polygonComObj.entityList = []

      return graphical
      }
      return null
    }, Cesium.ScreenSpaceEventType.LEFT_DOUBLE_CLICK)
  }

  // 修改圆形边界线生成函数
  private generateCirclePositions (center: Cesium.Cartesian3, radius: number): Cesium.Cartesian3[] {
    const positions: Cesium.Cartesian3[] = []

    if (!center || radius <= 0) return positions

    try {
      const cartographic = Cesium.Cartographic.fromCartesian(center)
      if (!cartographic) return positions

      const lat = cartographic.latitude
      const lon = cartographic.longitude
      const height = cartographic.height

      const earthRadius = 6378137.0
      const angularDistance = radius / earthRadius

      for (let i = 0; i <= 360; i += 15) {
        const angle = Cesium.Math.toRadians(i)

        const newLat = Math.asin(
          Math.sin(lat) * Math.cos(angularDistance) +
          Math.cos(lat) * Math.sin(angularDistance) * Math.cos(angle)
        )

        const newLon = lon + Math.atan2(
          Math.sin(angle) * Math.sin(angularDistance) * Math.cos(lat),
          Math.cos(angularDistance) - Math.sin(lat) * Math.sin(newLat)
        )

        const newPosition = Cesium.Cartesian3.fromRadians(newLon, newLat, height)
        if (newPosition && this._isValidPosition(newPosition)) {
          positions.push(newPosition)
        }
      }

      if (positions.length > 0 && !Cesium.Cartesian3.equals(positions[0], positions[positions.length - 1])) {
        positions.push(positions[0])
      }
    } catch (error) {
      console.error('Error generating circle positions:', error)
      return this.generateCirclePositionsFallback(center, radius)
    }

    return positions
  }

  // 备用方法：平面近似
  private generateCirclePositionsFallback (center: Cesium.Cartesian3, radius: number): Cesium.Cartesian3[] {
    const positions: Cesium.Cartesian3[] = []

    if (!center || radius <= 0) return positions

    const normal = Cesium.Ellipsoid.WGS84.geodeticSurfaceNormal(center)
    if (!normal) return positions

    const tangent = Cesium.Cartesian3.cross(Cesium.Cartesian3.UNIT_Z, normal, new Cesium.Cartesian3())
    Cesium.Cartesian3.normalize(tangent, tangent)
    const bitangent = Cesium.Cartesian3.cross(normal, tangent, new Cesium.Cartesian3())
    Cesium.Cartesian3.normalize(bitangent, bitangent)

    for (let i = 0; i <= 360; i += 15) {
      const angle = Cesium.Math.toRadians(i)
      const x = Math.cos(angle) * radius
      const y = Math.sin(angle) * radius

      const offset = new Cesium.Cartesian3()
      Cesium.Cartesian3.multiplyByScalar(tangent, x, offset)
      Cesium.Cartesian3.add(offset, Cesium.Cartesian3.multiplyByScalar(bitangent, y, new Cesium.Cartesian3()), offset)
      Cesium.Cartesian3.add(center, offset, offset)

      if (this._isValidPosition(offset)) {
        positions.push(offset)
      }
    }

    if (positions.length > 0 && !Cesium.Cartesian3.equals(positions[0], positions[positions.length - 1])) {
      positions.push(positions[0])
    }

    return positions
  }

  /**
   * 通过实体查找绘图对象
   */
  getDrawingByEntity (entity: any): any {
    return this._findDrawingByEntity(entity)
  }

  /**
   * 移除绘制鼠标事件
   */
  private _removeDrawEvents (): void {
    if (this._drawHandler) {
      this._drawHandler.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_CLICK)
      this._drawHandler.removeInputAction(Cesium.ScreenSpaceEventType.MOUSE_MOVE)
      this._drawHandler.removeInputAction(Cesium.ScreenSpaceEventType.RIGHT_CLICK)
      this._drawHandler.destroy()
      this._drawHandler = null
    }
  }

  /**
   * 清除当前绘制
   */
  clearCurrentDrawing (): void {
    this._removeDrawEvents()

    if (this._currentDrawingEntity) {
      this._dataSource.entities.remove(this._currentDrawingEntity)
      this._currentDrawingEntity = null
    }

    this._tempPositions = []
    this._mousePos = null
    this._drawType = null
    this._isDrawing = false
    this._isSelectionEnabled = true
    this._currentInteractionMode = 'none'
    this._restoreCameraSettings()
  }

  /**
   * 删除指定图形
   */
  deleteDrawing (entity: Cesium.Entity): boolean {
    if (!entity) return false

    // 如果删除的是选中的图形，先取消选择
    if (this._selectedDrawing && this._selectedDrawing.entity === entity) {
      this.deselectDrawing()
    }

    // 从数据源中删除
    const removed = this._dataSource!.entities.remove(entity)

    // 从绘图列表中删除
    if (removed) {
      const index = this._drawings.findIndex(d => d.entity === entity)
      if (index !== -1) {
        this._drawings.splice(index, 1)
      }

      // 从API图形中删除
      this._apiGraphics.forEach((graphic, id) => {
        if (graphic.entity === entity) {
          this._apiGraphics.delete(id)
        }
      })
    }

    return removed
  }

  /**
   * 通过ID删除图形
   */
  deleteDrawingById (id: string): boolean {
    const index = this._drawings.findIndex(d => d.id === id)
    if (index !== -1) {
      const drawing = this._drawings[index]
      const removed = this._dataSource!.entities.remove(drawing.entity)

      if (removed) {
        this._drawings.splice(index, 1)
      }

      // 从API图形中删除
      this._apiGraphics.delete(id)

      return removed
    }

    return false
  }

  /**
   * 获取所有绘制的图形
   */
  getDrawings (): any[] {
    return this._drawings
  }

  /**
   * 获取当前选中的图形
   */
  getSelectedDrawing (): any {
    return this._selectedDrawing
  }

  /**
   * 通过ID获取图形
   */
  getDrawingById (id: string): any {
    // 首先从绘图中查找
    const drawing = this._drawings.find(d => d.id === id)
    if (drawing) return drawing

    // 从API图形中查找
    return this._apiGraphics.get(id)
  }

  /**
   * 通过ID选择图形
   */
  selectDrawingById (id: string): boolean {
    const drawing = this.getDrawingById(id)
    if (drawing) {
      this.selectDrawing(drawing)
      return true
    }
    return false
  }

  /**
   * 清除所有图形
   */
  clearAll (): void {
    this.deselectDrawing()
    this.clearCurrentDrawing()
    this._removeFrustum()
    this._clearEntities()
    this._dataSource.entities.removeAll()
    this._drawings = []
    this._apiGraphics.clear()

    // 重置所有状态
    this._isDragging = false
    this._draggingEntity = null
    this._draggingIsVirtual = false
    this._virtualPointIndex = -1

    // 重置起始点状态
    this._hasStartingPoint = false
    this._startingPoint = null

    // 重置相机控制
    this.graphicalEntities.forEach(entity => {
      this._dataSource.entities.remove(entity.entity)
    })
    this.graphicalEntities = []
    this.currentGraphical = null
  }

  /**
   * 转化为真实点位经纬度
   */
  conversion (cartesian: Cesium.Cartesian3 | Cesium.Cartesian3[]): any {
    if (cartesian instanceof Array) {
      return cartesian.map(item => this.conversion(item))
    } else {
      const cartographic = Cesium.Cartographic.fromCartesian(cartesian)
      const pointInfo: any = {}
      pointInfo.longitude = Cesium.Math.toDegrees(cartographic.longitude)
      pointInfo.latitude = Cesium.Math.toDegrees(cartographic.latitude)
      let height = cartographic.height

      try {
        const terrainHeight = this.viewer.scene.globe.getHeight(cartographic)
        if (terrainHeight !== undefined && !isNaN(terrainHeight)) {
          height = terrainHeight
        }
      } catch (e) {
        console.warn('获取地形高度失败，使用椭球高度:', e)
      }

      pointInfo.height = Math.max(0, Math.floor(height))
      return pointInfo
    }
  }

  /**
   * 点位样式选项（支持放大显示）
   */
  pointOption (index: number, isDrawing: boolean = false): any {
    const baseSize = 26
    const size = isDrawing ? baseSize * 1.5 : baseSize

    return {
      billboard: {
        image: 'data:image/svg+xml;base64,' +
          btoa(`<svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 50 50">
                     <circle cx="25" cy="25" r="20" fill="#1E90FF" stroke="#00008B" stroke-width="1"/>
                     <text x="25" y="25" font-size="16" text-anchor="middle" fill="white" dy=".3em">${index}</text>
                 </svg>`),
        width: size,
        height: size,
        heightReference: Cesium.HeightReference.NONE,
        pixelOffset: new Cesium.Cartesian2(0, 5),
        horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
        verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
        distanceDisplayCondition: new Cesium.DistanceDisplayCondition(0, 500000)
      }
    }
  }

  /**
   * 添加实体
   */
  private _addEntity (options: any, type?: string, pointsList?: any): Cesium.Entity {
    const entity = this._dataSource!.entities.add(options)
    this.entities.push(entity)

    if (type === 'point') {
      const pointInfo = this.conversion(options.position)
      pointInfo.id = pointsList.length + 1
      options.pointInfo = pointInfo
      pointsList.push(options)
    }

    return entity
  }

  /**
   * 新增点位（支持放大显示）
   */
  async addEntity (position: any, isDrawing: boolean = false, pointsList?: any): Promise<void> {
    const { longitude, latitude, height } = position
    const targetPointsList = pointsList || this.entitiesPointOptions

    const cartesianPosition = Cesium.Cartesian3.fromDegrees(longitude, latitude, height)

    const entity = await this._addEntity(
      {
        position: cartesianPosition,
        ...this.pointOption(targetPointsList.length + 1, isDrawing),
        polyline: {
          positions: new Cesium.CallbackProperty(() => {
            return [
              Cesium.Cartesian3.fromDegrees(longitude, latitude, 0),
              Cesium.Cartesian3.fromDegrees(longitude, latitude, height),
            ]
          }, false),
          material: new Cesium.StripeMaterialProperty({ ...stripeMaterialPropertyOption }),
          width: 5,
          clampToGround: false,
        },
      },
      'point', targetPointsList
    )

    const drawing = {
      id: `point_${Date.now()}`,
      type: 'LinePoint',
      entity: entity,
      actionGroup: [],
      positions: [cartesianPosition]
    }

    this._drawings.push(drawing)
  }

  /**
   * 新增视锥体
   */
  private _addFrustum (position: any, params: any = { heading: BASE_HEADING, pitch: BASE_PITCH, fov: BASE_FOV }): void {
    const options = JSON.parse(JSON.stringify(params))
    const { height, longitude, latitude } = position

    options.fov = calculateFov(options.fov)
    let far = height * 1.5 || BASE_FAR
    if (height) {
      if (height > BASE_NEAR) {
        far = (height * 1.5 || BASE_FAR) - BASE_NEAR
      } else {
        far = height * 1.5 || BASE_FAR
      }
    }

    const aspectRatio = 1.5
    const frustum = new Cesium.PerspectiveFrustum({
      fov: Cesium.Math.toRadians(options.fov),
      aspectRatio: aspectRatio,
      near: BASE_NEAR,
      far: far,
    })

    const scene = this.viewer.scene
    this.currentPrimitivesCamera = new Cesium.Camera(scene)

    this.currentPrimitivesCamera.frustum = frustum

    this.currentPrimitivesCamera.setView({
      destination: Cartesian3.fromDegrees(longitude, latitude, height),
      orientation: {
        heading: Cesium.Math.toRadians(options.heading),
        pitch: Cesium.Math.toRadians(options.pitch),
        roll: BASE_ROLL,
      },
      up: Cartesian3.UNIT_Z,
    })

    this._updateFrustum()
  }

  /**
   * 更新视锥体
   */
  private _updateFrustum (): void {
    const scene = this.viewer.scene
    this._removeFrustum()

    this.currentPrimitive = new (Cesium as any).DebugCameraPrimitive({
      camera: this.currentPrimitivesCamera,
      color: Color.WHITE,
      updateOnChange: false,
    })

    scene.primitives.add(this.currentPrimitive)
  }

  /**
   * 移除视锥体
   */
  private _removeFrustum (): void {
    if (state.frustumTool) {
      state.frustumTool.removeAllFrustums()
    }
  }

  /**
   * 移除实体
   */
  private _removeEntity (entity: Cesium.Entity): boolean {
    if (!this.viewer) return false

    const index = this.entities.indexOf(entity)
    if (index !== -1) {
      this._dataSource.entities.remove(entity)
      this.entities.splice(index, 1)
      return true
    }
    return false
  }

  /**
   * 清除所有实体
   */
  private _clearEntities (): void {
    if (!this.viewer) return

    this._dataSource.entities.removeAll()
    this.entities = []
    this.entitiesPointOptions = []
  }

  /**
   * 设置相机位置
   */
  flyTo (position: any, orientation: any, options: any = {}): void {
    if (!this.viewer) return

    const defaultOptions = {
      destination: Cartesian3.fromDegrees(
        position.longitude,
        position.latitude,
        position.height || 1000
      ),
      orientation: {
        heading: (orientation && orientation.heading) ? Cesium.Math.toRadians(orientation.heading) : Cesium.Math.toRadians(0),
        pitch: (orientation && orientation.pitch) ? Cesium.Math.toRadians(orientation.pitch) : Cesium.Math.toRadians(IDEAL_BASE_PITCH),
        roll: 0
      },
      duration: 1,
      ...options
    }

    this.viewer.camera.flyTo(defaultOptions)
  }

  // 在 addEntityLine 方法中，确保航线被正确选中
  addEntityLine (isDrawing: boolean = false, pointsList?: any[]): void {
    const targetPointsList = pointsList || this.entitiesPointOptions

    if (targetPointsList && targetPointsList.length >= 1 && this._startingPoint) {
      const positions = [this._startingPoint]
      targetPointsList.forEach((item: any) => {
        positions.push(item.position)
      })

      const polyline = this._addEntity({
        polyline: {
          positions,
          ...polylineOption
        },
      }) as any

      // 标记为绘制中的实体
      if (isDrawing) {
        polyline._isDrawing = true
      }

      const drawing = {
        id: `polyline_${Date.now()}`,
        type: 'PointLine',
        entity: polyline,
        positions: positions
      }
      this._drawings.push(drawing)

      // 确保航线被选中
      this.selectDrawing(drawing)

      // 如果是绘制中，缩放以显示所有点
      if (isDrawing) {
        this.viewer.zoomTo(polyline)
      }
    }
  }

  /**
   * 新增点位视锥体
   */
  addFrustum (position: any): void {
    // 确保 state.frustumTool 已初始化
    if (state.frustumTool) {
      const currentPosition = Cartesian3.fromDegrees(position.longitude, position.latitude, position.height)

      // 提供完整的参数
      state.frustumTool.addFrustumToWaypoint(
        this._drawings[this._drawings.length - 1]?.entity?.id, // waypointId
        currentPosition, // position
        {
          heading: 0,
          pitch: 60,
          roll: 0,
          fov: 60,
          near: 10,
          far: 2000,
          aspectRatio: 1.5,
          color: Cesium.Color.fromCssColorString('#4cc9f0'),
          showModel: false,
          showFrustum: true,
          frustumLineShow: true,
          outlineShow: true,
          frustumShow: true
        })
    }
  }

  /**
   * 画点
   */
  private _addPoint (p: number[]): Cesium.Entity {
    return this._dataSource!.entities.add({
      position: Cesium.Cartesian3.fromDegrees(p[0], p[1], p[2]),
      point: {
        ...this._styles.point
      },
    })
  }

  /**
   * 销毁工具，释放资源
   */
  destroy (): void {
    this._removeDrawEvents()
    this.disableInteractions()

    if (this._showLocationHandler && !this._showLocationHandler.isDestroyed()) {
      this._showLocationHandler.destroy()
      this._showLocationHandler = null
    }

    // 移除UI元素
    if (this._infoLocationPanel && this.viewer.container.contains(this._infoLocationPanel)) {
      this.viewer.container.removeChild(this._infoLocationPanel)
      this._infoLocationPanel = null
    }

    if (this._showRightMenuPanel && this.viewer.container.contains(this._showRightMenuPanel)) {
      this.viewer.container.removeChild(this._showRightMenuPanel)
      this._showRightMenuPanel = null
    }

    // 销毁导航控件
    this._destroyNavigationControls()

    // 恢复相机设置
    this._restoreCameraSettings()

    this.clearAll()
    this.viewer.dataSources.remove(this._dataSource)

    // console.log('DrawTool 已销毁')
  }

  /** 更新位置 */
  public updatePositionById (id:string, position:Cesium.Cartesian3|{longitude:number;latitude:number;height:number;radius:number}, type = 'point'):void {
    const entity = this.getDataSourceEntity(id)
    if (!entity) return

    // 更新实体位置
    if (type === 'point') {
      entity.position = position
    } else {
      const center = Cesium.Cartesian3.fromDegrees(position.longitude, position.latitude, position.height || 0)
      const positions = this.calculateCirclePoints(position, position.radius)
      entity.position = center
      entity.polygon.hierarchy = new Cesium.PolygonHierarchy(positions)
      entity.polyline.positions = positions
    }
  }

  public updateCirclePosRadiusById (id:string, position:{longitude:number;latitude:number;height:number;radius:number}):void {
    const entity = this.getDataSourceEntity(id)
    if (!entity) return
    const drawing = this._selectedDrawing
    // 更新实体位置和半径
    const center = Cesium.Cartesian3.fromDegrees(position.longitude, position.latitude)
    const radiusLon = position.longitude + (position.radius / (111320 * Math.cos(position.latitude * Math.PI / 180)))
    const radiusPoint = {
      longitude: radiusLon,
      latitude: position.latitude,
      height: 0
    }
    const newRadiusPoint = Cesium.Cartesian3.fromDegrees(
      radiusPoint.longitude,
      radiusPoint.latitude,
      radiusPoint.height
    )
    drawing.positions[0] = center
    drawing.positions[1] = newRadiusPoint
    // 更新圆形边界
    this._updateCircleGeometry(drawing)

    // 更新编辑点位置
    this._updateCircleEditPoints()

    // 更新图形属性
    this._updateDrawingAttributes(drawing)
  }

  /** 更新颜色 */
  public updateColorById (id: string, color: string): void {
    const selectDrawing = this.getAPIGraphicById(id)
    if (!selectDrawing) return
    const entity = selectDrawing?.entity
    selectDrawing.origin.resource.content.properties.color = color
    selectDrawing.originalColor = color
    if (entity.typeInfo === 'polyline') {
      // 检查是否已经存在PolylineOutlineMaterialProperty实例
      entity.polyline.material = new Cesium.PolylineOutlineMaterialProperty({
        color: Cesium.Color.fromCssColorString(color),
        outlineWidth: 2,
        outlineColor: Cesium.Color.WHITE
      })
      this._draggablePoints.forEach(point => {
        point.point.color = Cesium.Color.fromCssColorString(color)
      })
    } else if (entity.typeInfo === 'polygon') {
      // 对于polygon，直接更新material
      entity.polyline.material = new Cesium.PolylineOutlineMaterialProperty({
        color: Cesium.Color.fromCssColorString(color),
        outlineWidth: 2,
        outlineColor: Cesium.Color.WHITE
      })
      entity.polygon.material = Cesium.Color.fromCssColorString(color).withAlpha(0.6)
      this._draggablePoints.forEach(point => {
        point.point.color = Cesium.Color.fromCssColorString(color)
      })
    } else if (entity.typeInfo === 'point') {
      entity.point.color = Cesium.Color.fromCssColorString(color)
    } else {
      entity.polygon.material = Cesium.Color.fromCssColorString(color).withAlpha(0.6)
      entity.polyline.material = new Cesium.PolylineOutlineMaterialProperty({
        color: Cesium.Color.fromCssColorString(color),
        outlineWidth: 2,
        outlineColor: Cesium.Color.WHITE
      })
      this._draggablePoints.forEach(point => {
        point.point.color = Cesium.Color.fromCssColorString(color)
      })
      if (this.circleComObj.circleEntity) {
        this.circleComObj.circleEntity.polyline.material = new Cesium.PolylineDashMaterialProperty({
          color: Cesium.Color.fromCssColorString(color),
        })
      }
    }
  }

  updateDrawing (drawing: any): void {
    this._drawings = this._drawings.map(d => {
      if (d.id === drawing.id) {
        return drawing
      }
      return d
    })
  }

  // 新增：计算几何中心（用于多边形/圆形标签位置）
  private calcCenter (positions: Cesium.Cartesian3[]): Cesium.Cartesian3 {
    let x = 0; let y = 0; let z = 0
    positions.forEach(pos => {
      x += pos.x
      y += pos.y
      z += pos.z
    })
    const count = positions.length
    return new Cesium.Cartesian3(x / count, y / count, z / count)
  }

  /**
 * 根据中心点和半径计算圆形边缘点
 * @param {Cartesian3} center - 中心点坐标（笛卡尔坐标）
 * @param {number} radius - 半径（米）
 * @param {number} [segments=64] - 圆弧分段数，默认64
 * @returns {Cartesian3[]} 圆形边缘点数组
 */
  private getCirclePoints (center, radius, segments = 64) {
    const points = []

    for (let i = 0; i <= segments; i++) {
      // 计算角度（弧度）
      const angle = (i / segments) * Math.PI * 2

      // 计算偏移量（在正切空间）
      const x = Math.cos(angle) * radius
      const y = Math.sin(angle) * radius

      // 获取中心点的东北上坐标系
      const transform = Cesium.Transforms.eastNorthUpToFixedFrame(center)

      // 创建偏移向量
      const offset = new Cesium.Cartesian3(x, y, 0)

      // 将偏移转换到世界坐标系
      const point = Cesium.Matrix4.multiplyByPoint(transform, offset, new Cesium.Cartesian3())

      points.push(point)
    }

    return points
  }

  getCircleBorderCoordinate (centerPoint: { lng: number; lat: number; }, mousePoint: { lng: number; lat: number; }, radius: number) {
    // 计算圆心到鼠标点的方向角度
    const dLng = mousePoint.lng - centerPoint.lng
    const dLat = mousePoint.lat - centerPoint.lat

    // 计算角度（弧度）
    const angle = Math.atan2(dLat, dLng)

    // 根据角度和半径计算边界点位置
    const borderLng = centerPoint.lng + (radius / (111320 * Math.cos(centerPoint.lat * Math.PI / 180))) * Math.cos(angle)
    const borderLat = centerPoint.lat + (radius / 111320) * Math.sin(angle)

    return {
      lng: borderLng,
      lat: borderLat,
      height: this.getTerrainHeight({ lng: borderLng, lat: borderLat }, this.viewer)
    }
  }

  // 优化后的 addAPIGraphic 方法
  addAPIGraphic (apiGraphic: APIGraphicalData): GraphicalEntity {
    const positions = apiGraphic.positions.map(pos =>
      Cesium.Cartesian3.fromDegrees(pos.longitude, pos.latitude, pos.height || 0)
    )
    let entity: Cesium.Entity
    const color = apiGraphic.attributes?.color || '#2d8cf0'

    // 计算图形的额外属性
    let additionalAttributes = {}

    switch (apiGraphic.type) {
      case 'Point': {
        entity = this._dataSource.entities.add({
          id: apiGraphic.id,
          position: positions[0],
          point: { ...this._styles.point, color: Cesium.Color.fromCssColorString(color) }
        })
        entity.typeInfo = 'point'
        break
      }
      case 'Polyline': {
        entity = this._dataSource.entities.add({
          id: apiGraphic.id,
          polyline: {
            positions: positions,
            ...this._styles.polyline,
            material: Cesium.Color.fromCssColorString(color)
          }
        }) as PolylineEntity
        (entity as PolygonEntity).isFromAPI = true
        entity.typeInfo = 'polyline'
        break
      }
      case 'Polygon': {
        // 计算多边形面积和周长
        const polygonArea = this.calcPolygonArea(positions)
        const perimeter = this.calcPolylineLength([...positions, positions[0]])
        additionalAttributes = { horizontalArea: polygonArea, perimeter: perimeter }

        const polygonColor = Cesium.Color.fromCssColorString(color) as any
        entity = this._dataSource.entities.add({
          id: apiGraphic.id,
          polygon: {
            hierarchy: positions,
            ...this._styles.polygon,
            material: Cesium.Color.fromAlpha(polygonColor, 0.5)
          },
          polyline: {
            positions: positions,
            ...this._styles.polyline,
            material: Cesium.Color.fromCssColorString(color)
          }
        }) as PolygonEntity

        (entity as PolygonEntity).isFromAPI = true
        entity.typeInfo = 'polygon'
        break
      }
      case 'Circle': {
        // console.log('处理圆形', apiGraphic)

        // 确保有足够的点
        let positions = apiGraphic.positions
        if (positions.length < 2) {
          // 只有一个中心点，需要创建半径点
          const center = positions[0]
          const radius = apiGraphic.radius || 100

          // 计算正东方向的半径点
          const cartographic = Cesium.Cartographic.fromDegrees(
            center.longitude,
            center.latitude,
            center.height || 0
          )

          // 计算半径点的经纬度
          const radiusLon = center.longitude + (radius / (111320 * Math.cos(center.latitude * Math.PI / 180)))
          const radiusPoint = {
            longitude: radiusLon,
            latitude: center.latitude,
            height: center.height || 0
          }

          // 添加到数组
          positions = [center, radiusPoint]
          apiGraphic.positions = positions
        }

        const centerCartesian = Cesium.Cartesian3.fromDegrees(
          positions[0].longitude,
          positions[0].latitude,
          positions[0].height
        )
        const radiusPointCartesian = Cesium.Cartesian3.fromDegrees(
          positions[1].longitude,
          positions[1].latitude,
          positions[1].height
        )

        // 计算半径
        const radius = Cesium.Cartesian3.distance(centerCartesian, radiusPointCartesian)

        // 使用 draw-circle.ts 中的方法计算圆形边界
        const circlePositions = this.calculateCirclePoints(centerCartesian, radius)

        // 创建圆形实体
        const circleColor = Cesium.Color.fromCssColorString(color)
        entity = this._dataSource.entities.add({
          id: apiGraphic.id,
          position: centerCartesian, // 实体位置设置为圆心
          polygon: {
            hierarchy: new Cesium.PolygonHierarchy(circlePositions),
            material: circleColor.withAlpha(0.4),
            classificationType: Cesium.ClassificationType.BOTH
          },
          polyline: {
            positions: circlePositions,
            width: 3,
            material: circleColor,
            clampToGround: false
          }
        })

        entity.typeInfo = 'circle'
        // 将圆心和半径点都存储在 positions 中
        const graphicPositions = [centerCartesian, radiusPointCartesian]
        apiGraphic.positionsList = graphicPositions

        break
      }
      default:
        console.error('Unknown graphic type:', apiGraphic.type)
        return null
    }

    // 合并基础属性和额外属性
    const attributes = {
      ...apiGraphic.attributes,
      ...additionalAttributes
    }

    const graphical: GraphicalEntity = {
      type: apiGraphic.type,
      entity: entity,
      positions: positions,
      attributes: attributes,
      latlngList: apiGraphic.positions,
      isFromAPI: true,
      originalColor: color,
      origin: apiGraphic.origin
    }

    // 添加到API图形存储
    this._apiGraphics.set(apiGraphic.id, graphical)

    // 添加到绘图列表
    const drawing = {
      id: apiGraphic.id,
      type: apiGraphic.type,
      entity: entity,
      positions: positions,
      attributes: attributes,
      latlngList: apiGraphic.positions,
      isFromAPI: true,
      originalColor: color,
      apiGraphic: apiGraphic
    }
    this._drawings.push(drawing)

    return graphical
  }

  private calculateCirclePoints (point: { longitude: number; latitude: number; height?: number }, radius: number, steps: number = 64): Cesium.Cartesian3[] {
    const positions: Cesium.Cartesian3[] = []
    let cartesian3 = point
    if (point.x) {
      cartesian3 = this.conversion(point)
    }
    for (let i = 0; i < steps; i++) {
      const angle = (i / steps) * 2 * Math.PI

      // 使用球面坐标计算圆上的点
      const lat = cartesian3.latitude + (radius / 111320) * Math.cos(angle)
      const lng = cartesian3.longitude + (radius / (111320 * Math.cos(cartesian3.latitude * Math.PI / 180))) * Math.sin(angle)
      const height = this.getTerrainHeight({ lng, lat }, this.viewer) || cartesian3.height || 0

      positions.push(Cesium.Cartesian3.fromDegrees(lng, lat, height))
    }

    // 闭合圆形
    if (positions.length > 0) {
      positions.push(positions[0].clone())
    }

    return positions
  }

  private calculateCirclePointsFallback (center: Cesium.Cartesian3, radius: number, segments: number = 64): Cesium.Cartesian3[] {
    const positions: Cesium.Cartesian3[] = []

    if (!center || radius <= 0) return positions

    try {
    // 创建东北上坐标系
      const transform = Cesium.Transforms.eastNorthUpToFixedFrame(center)

      for (let i = 0; i <= segments; i++) {
        const angle = (i / segments) * Math.PI * 2

        // 计算在东北平面上的偏移
        const x = Math.cos(angle) * radius
        const y = Math.sin(angle) * radius

        // 创建偏移向量
        const offset = new Cesium.Cartesian3(x, y, 0)

        // 转换到世界坐标系
        const point = Cesium.Matrix4.multiplyByPoint(transform, offset, new Cesium.Cartesian3())

        if (this._isValidPosition(point)) {
          positions.push(point)
        }
      }

      // 闭合圆形
      if (positions.length > 0 && !Cesium.Cartesian3.equals(positions[0], positions[positions.length - 1])) {
        positions.push(positions[0])
      }
    } catch (error) {
      console.error('Error in circle points fallback:', error)
    }

    return positions
  }

  /**
     * 获取指定经纬度的地形高度
     * @param {Object} position - 包含 lng, lat 的位置对象
     * @param {Object} viewer - Cesium 查看器实例
     * @returns {number} 地形高度（米），精度7位小数
     */
  private getTerrainHeight (position: { lng: number; lat: number }, viewer: Cesium.Viewer): number {
    const { lng, lat } = position
    const cartographic = Cesium.Cartographic.fromDegrees(lng, lat, 0)
    const terrainHeight = viewer.scene.globe.getHeight(cartographic)
    return Number.parseFloat((terrainHeight ?? 0).toFixed(7))
  }

  // 新增：批量添加API图形
  addAPIGraphics (apiGraphics: APIGraphicalData[]): GraphicalEntity[] {
    const results: GraphicalEntity[] = []
    apiGraphics.forEach(graphic => {
      const result = this.addAPIGraphic(graphic)
      if (result) {
        results.push(result)
      }
    })
    return results
  }

  // 新增：获取所有API图形
  getAPIGraphics (): Map<string, GraphicalEntity> {
    return this._apiGraphics
  }

  // 新增：根据ID获取API图形
  getAPIGraphicById (id: string): GraphicalEntity | undefined {
    return this._apiGraphics.get(id)
  }

  // 新增：更新API图形
  public updateAPIGraphic (id: string, apiResponse: any): void {
    const entity = this.getDrawingByEntity(id)
    if (!entity) return

    // 使用缓存的材质
    if (entity.polyline) {
      entity.polyline.positions = apiResponse.positions
      entity.polyline.width = apiResponse.width
      entity.polyline.material = this._getPolylineMaterial(apiResponse.color)
    } else if (entity.polygon) {
      entity.polygon.hierarchy = apiResponse.hierarchy
      entity.polygon.material = Cesium.Color.fromCssColorString(apiResponse.color).withAlpha(0.6)
    }
  }

  // 新增：删除API图形
  deleteAPIGraphic (id: string): boolean {
    const graphic = this._apiGraphics.get(id)
    if (!graphic) return false

    // 从数据源中删除实体
    const removed = this._dataSource.entities.remove(graphic.entity)
    if (removed) {
      this._apiGraphics.delete(id)

      // 从绘图列表中删除
      const index = this._drawings.findIndex(d => d.id === id)
      if (index !== -1) {
        this._drawings.splice(index, 1)
      }
    }
    this.deselectDrawing()
    return removed
  }

  // 新增：将API返回的数据转换为APIGraphicalData
  convertAPIResponseToGraphicalData (apiResponse: APIResponseData): APIGraphicalData {
    const { id, resource } = apiResponse
    const { type, content } = resource
    const { geometry, properties } = content
    const { radius } = geometry
    // 转换坐标格式
    let positions = this.convertGeoJSONCoordinates(geometry.coordinates)

    // 获取图形类型
    const graphicType = this.mapResourceTypeToGraphicType(type, geometry.type)

    // 构建属性
    const attributes = this.buildAttributes(properties, graphicType)
    // 在 convertAPIResponseToGraphicalData 方法中，确保圆形数据正确
    if (geometry.type === 'Circle') {
      // 圆形数据
      const coordinates = geometry.coordinates as number[]
      const radius = geometry.radius || 100

      const center = {
        longitude: coordinates[0],
        latitude: coordinates[1],
        height: coordinates[2] || 0
      }

      // 计算半径点（正东方向）
      const radiusPoint = {
        longitude: center.longitude + (radius / (111320 * Math.cos(center.latitude * Math.PI / 180))),
        latitude: center.latitude,
        height: center.height
      }

      positions = [center, radiusPoint]

      return {
        id: id,
        type: 'Circle',
        positions: positions,
        radius: radius,
        attributes: {
          color: properties?.color || '#2d8cf0'
        },
        properties: properties,
        origin: apiResponse
      }
    }

    return {
      id: id,
      radius: radius,
      type: graphicType,
      positions: positions,
      attributes: attributes,
      properties: properties,
      origin: apiResponse
    }
  }

  clearPointLine (): void {
    this._removeFrustum()
    this._removeDrawEvents()
    const drawing = this._drawings.filter(d => d.type === 'PointLine' || d.type === 'PointPolygon' || d.type === 'LinePoint' || d.type === 'StartingPoint')
    if (drawing.length) {
      drawing.forEach(el => {
        this.deleteDrawing(el.entity)
      })
    }
  }

  /** 返回api数据 */
  convertGraphicalDataToAPIResponse (graphic: APIGraphicalData): APIResponseData {
    return graphic.origin as APIResponseData
  }

  // 新增：转换GeoJSON坐标到位置数组
  private convertGeoJSONCoordinates (coordinates: number[] | number[][] | number[][][]): { longitude: number; latitude: number; height: number }[] {
    const positions: { longitude: number; latitude: number; height: number }[] = []

    if (Array.isArray(coordinates)) {
      if (coordinates.length === 0) return positions

      // 处理点
      if (typeof coordinates[0] === 'number') {
        const [longitude, latitude, height = 0] = coordinates as number[]
        positions.push({ longitude, latitude, height })
      } else if (Array.isArray(coordinates[0])) {
        // 处理线或多边形
        if (typeof coordinates[0][0] === 'number') {
          // 线
          (coordinates as number[][]).forEach(coord => {
            const [longitude, latitude, height = 0] = coord
            positions.push({ longitude, latitude, height })
          })
        } else if (Array.isArray(coordinates[0][0])) {
          // 多边形（取第一个环）
          const polygon = (coordinates as number[][][])[0]
          polygon.forEach(coord => {
            const [longitude, latitude, height = 0] = coord
            positions.push({ longitude, latitude, height })
          })
        }
      }
    }

    return positions
  }

  // 新增：映射资源类型到图形类型
  private mapResourceTypeToGraphicType (resourceType: number, geometryType: string): 'Point' | 'Polyline' | 'Polygon' | 'Circle' {
    // 优先使用resource.type（数字）
    switch (resourceType) {
      case MapElementEnum.PIN: // 0
        return 'Point'
      case MapElementEnum.LINE: // 1
        return 'Polyline'
      case MapElementEnum.POLY: // 2
        return 'Polygon'
      case 3: // 圆形（假设3是圆形）
        return 'Circle'
      default:
        return this.mapGeometryTypeToGraphicType(geometryType)
    }
  }

  // 新增：映射Geometry类型到图形类型
  private mapGeometryTypeToGraphicType (geometryType: string): 'Point' | 'Polyline' | 'Polygon' | 'Circle' {
    switch (geometryType) {
      case 'Point':
        return 'Point'
      case 'LineString':
        return 'Polyline'
      case 'Polygon':
        return 'Polygon'
      case 'Circle':
        return 'Circle'
      default:
        console.warn(`Unknown geometry type: ${geometryType}, defaulting to Point`)
        return 'Point'
    }
  }

  // 新增：构建属性
  private buildAttributes (properties: any, graphicType: string): PointAttributes | PolylineAttributes | PolygonAttributes {
    const baseAttributes = {
      color: properties?.color || '#2d8cf0'
    }

    switch (graphicType) {
      case 'Point':
        return {
          ...baseAttributes,
          text: properties?.text,
          imgUrl: properties?.imgUrl
        } as PointAttributes
      case 'Polyline':
        return baseAttributes as PolylineAttributes
      case 'Polygon':
      case 'Circle':
        return baseAttributes as PolygonAttributes
      default:
        return baseAttributes as PointAttributes
    }
  }

  // 新增：批量添加API返回的图形
  addAPIResponseGraphics (apiResponses: APIResponseData[]): GraphicalEntity[] {
    const results: GraphicalEntity[] = []

    apiResponses.forEach(response => {
      try {
        const graphicData = this.convertAPIResponseToGraphicalData(response)
        const graphic = this.addAPIGraphic(graphicData)
        if (graphic) {
          results.push(graphic)
        }
      } catch (error) {
        console.error(`Failed to add API graphic ${response.id}:`, error)
      }
    })

    return results
  }

  // 新增：添加单个API返回的图形
  addAPIResponseGraphic (apiResponse: APIResponseData): GraphicalEntity | null {
    try {
      const graphicData = this.convertAPIResponseToGraphicalData(apiResponse)
      return this.addAPIGraphic(graphicData)
    } catch (error) {
      console.error(`Failed to add API graphic ${apiResponse.id}:`, error)
      return null
    }
  }
}
